import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import Button from '../Button/Button';
import FormulaireAdministratif from '../FormulaireAdministratif/FormulaireAdministratif';
import CreationArticle from '../CreationArticle/CreationArticle';
import ModalArticles from '../ModalArticles/ModalArticles';
import ArticlesService, { Article } from '../../services/articlesService';
import ValidationPopUp from '../ValidationPopUp/ValidationPopUp';
import CGVAccept from '../CGVAccept/CGVAccept';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import ConsultationPDF from '../../pages/PDF/ConsultationPDF';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole, isClientRole, isPrestataireRole, isSignataireRole } from '../../utils/roleUtils';
import './ConsultationBC.css';
import Coordonnees from '../Coordonnees/Coordonnees';
import { formatDate, formatPrice } from '../../utils/entityUtils';
import { PrintButton } from '../PrintButton/PrintButton';
import { getStatusColor } from '../../utils/entityUtils';
import { getLotImage } from '../../utils/getLotImage';



interface ConsultationBCProps {
  commande: any;
  rang: 1 | 2 | 3;
  errors: any;
  setErrors: React.Dispatch<React.SetStateAction<any>>;
  setCommande: React.Dispatch<React.SetStateAction<any>>;
  handleExport: () => void;
  validateConsultationFormBC: () => boolean;
  handleLancerConsultation: (rang: number) => Promise<void>;
  handleAccepterCommande: (rang: number) => Promise<void>;
  handleRefuserCommande: (rang: number) => Promise<void>;
  viewOnly?: boolean;
  acceptCGV?: boolean;
  setAcceptCGV?: React.Dispatch<React.SetStateAction<boolean>>;
  printRef?: any;
  commandeType?: 'MS' | 'BC' | 'BCI'; // NOUVEAU: pour identifier explicitement le type
}

const ConsultationBC: React.FC<ConsultationBCProps> = React.memo(({
  commande,
  rang,
  errors,
  setErrors,
  setCommande,
  handleExport,
  validateConsultationFormBC: validateConsultationFormBCProp,
  handleLancerConsultation,
  handleAccepterCommande,
  handleRefuserCommande,
  viewOnly = false,
  acceptCGV = false,
  setAcceptCGV = () => { },
  printRef,
  commandeType, // NOUVEAU: extraire commandeType
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isClientView = isClientRole(user?.role || '');
  const isPrestataireView = isPrestataireRole(user?.role || '');

  // NOUVEAU: Fonction pour vérifier si la date est expirée
  const isDateExpired = (dateStr: string): boolean => {
    if (!dateStr) return false;

    const [datePart, timePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);
    const targetDate = new Date(year, month - 1, day, hour, minute, second);
    const now = new Date();

    return now.getTime() >= targetDate.getTime();
  };

  // États existants
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [isArticlesModalOpen, setIsArticlesModalOpen] = useState(false);
  const [isEnvoyerPrestataireModalOpen, setIsEnvoyerPrestataireModalOpen] = useState(false);
  const [isChangeModeModalOpen, setIsChangeModeModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [articlesMode, setArticlesMode] = useState<'initial' | 'catalogue' | 'prestataire'>('initial');

  // NOUVEAU: État pour les articles existants de l'API
  const [existingArticles, setExistingArticles] = useState<Article[]>([]);
  const [loadingExistingArticles, setLoadingExistingArticles] = useState(false);

  // NOUVEAU: États pour les CGV
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [readyToLaunchAfterCGV, setReadyToLaunchAfterCGV] = useState(false);
  const [readyToAcceptAfterCGV, setReadyToAcceptAfterCGV] = useState(false); // NOUVEAU: Pour les prestataires
  const [readyToSendAfterCGV, setReadyToSendAfterCGV] = useState(false); // NOUVEAU: Pour l'envoi d'offre
  const [readyToTransfertAfterCGV, setReadyToTransfertAfterCGV] = useState(false); // NOUVEAU: Pour le transfert au prestataire (panier inversé)

  // NOUVEAU: États pour les modals prestataire transfert
  const [isEnvoyerOffreModalOpen, setIsEnvoyerOffreModalOpen] = useState(false);
  const [isRefuserTransfertModalOpen, setIsRefuserTransfertModalOpen] = useState(false);
  // NOUVEAU: confirmation pour consulter un prestataire de rang (ex: rang 2 après refus)
  const [isConfirmConsultationModalOpen, setIsConfirmConsultationModalOpen] = useState(false);
  const [pendingRangToConsult, setPendingRangToConsult] = useState<number | null>(null);

  // Ajouter les états pour la sélection du validateur
  const [validateurs, setValidateurs] = useState<any[]>([]);
  const [selectedValidateur, setSelectedValidateur] = useState<number | null>(null);
  const [validateursLoading, setValidateursLoading] = useState(false);

  // États pour la modal de notification au responsable
  const [isNotifyResponsableModalOpen, setIsNotifyResponsableModalOpen] = useState(false);
  const [responsables, setResponsables] = useState<any[]>([]);
  const [selectedResponsable, setSelectedResponsable] = useState<{ id: number, type: string } | null>(null);
  const [responsablesLoading, setResponsablesLoading] = useState(false);

  // NOUVEAU: Vérifier si une notification a déjà été envoyée à l'acheteur signataire
  // en regardant l'historique de l'opération pour un événement avec prefix "Notification" 
  // et type "BDC.Operation.{id}"
  const notificationInfo = useMemo(() => {
    const operationId = commande?.id;
    const historique = commande?.historique || [];

    if (!operationId || !Array.isArray(historique)) return { sent: false, labels: [] };

    // Chercher TOUS les événements de notification avec le bon format
    const notificationEvents = historique.filter((event: any) =>
      event.prefix === 'Demande de validation' &&
      event.type === `BDC.Operation.${operationId}`
    );

    console.log('🔔 Vérification demandes de validation déjà envoyées:', {
      operationId,
      historiqueLength: historique.length,
      notificationEvents,
      count: notificationEvents.length
    });

    if (notificationEvents.length > 0) {
      // Extraire les labels de toutes les notifications (triées par date décroissante - plus récentes en premier)
      const labels = notificationEvents
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .map((event: any) => event.label)
        .filter((label: string | null) => label !== null);
      return { sent: true, labels };
    }

    return { sent: false, labels: [] };
  }, [commande?.id, commande?.historique]);

  // NOUVEAU: État pour gérer l'expiration
  const [isOperationExpired, setIsOperationExpired] = useState(() => {
    // CORRECTION: Exclure les BCI (commandeType === 'BCI' ou source === 'BCI') de la logique d'expiration
    const source = commande?.source || commande?.operation?.source;

    console.log('🔍 ConsultationBC - Initialisation expiration:', {
      source,
      commandeType,
      commandeSource: commande?.source,
      operationSource: commande?.operation?.source,
      isBCI: commandeType === 'BCI' || source === 'BCI'
    });

    if (commandeType === 'BCI' || source === 'BCI') return false;

    const dateToCheck = commande?.expire_at;
    const statusId = commande?.status?.id;

    const isExpiredByStatus = (source === 'BC' && statusId === 9);
    const isExpiredByDate = dateToCheck ? isDateExpired(dateToCheck) : false;

    return isExpiredByStatus || isExpiredByDate;
  });  // NOUVEAU: Vérifier l'expiration au chargement et quand les dates ou statuts changent
  useEffect(() => {
    const dateToCheck = commande?.expire_at;
    const statusId = commande?.status?.id;
    const source = commande?.source || commande?.operation?.source;

    console.log('🔍 ConsultationBC - Vérification expiration:', {
      dateToCheck,
      statusId,
      source,
      commandeType,
      commandeSource: commande?.source,
      operationSource: commande?.operation?.source,
      isBCI: commandeType === 'BCI' || source === 'BCI',
      isExpiredByDate: dateToCheck ? isDateExpired(dateToCheck) : false,
      isExpiredByStatus: (source === 'BC' && statusId === 9),
      now: new Date().toISOString()
    });

    // CORRECTION: Exclure les BCI (commandeType === 'BCI' ou source === 'BCI') de l'expiration
    if (commandeType === 'BCI' || source === 'BCI') {
      setIsOperationExpired(false);
      return;
    }

    const isExpiredByStatus = (source === 'BC' && statusId === 9);
    const isExpiredByDate = dateToCheck ? isDateExpired(dateToCheck) : false;

    setIsOperationExpired(isExpiredByStatus || isExpiredByDate);
  }, [commande?.expire_at, commande?.status?.id, commande?.source, commande?.operation?.source, commandeType]);

  // NOUVEAU: Écouter l'événement d'expiration du chrono
  useEffect(() => {
    const handleChronoExpired = () => {
      console.log('📢 ConsultationBC: Chrono expiré');
      setIsOperationExpired(true);
    };

    window.addEventListener('chronoExpired', handleChronoExpired);

    return () => {
      window.removeEventListener('chronoExpired', handleChronoExpired);
    };
  }, []);

  // Fonction pour charger les validateurs (acheteurs signataires et admins)
  useEffect(() => {
    const fetchValidateurs = async () => {
      if (!commande?.organisme?.id) return;

      setValidateursLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/organismes`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Filtrer pour ne garder que les acheteurs signataires et admins
          const validateurs = data.data.filter((user: any) =>
            user.role === 'Acheteur signataire' ||
            user.role === 'Acheteur signataire admin'
          );
          setValidateurs(validateurs);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des validateurs:', error);
      } finally {
        setValidateursLoading(false);
      }
    };

    fetchValidateurs();
  }, [commande?.organisme?.id]);

  // NOUVEAU: Effet pour charger les articles existants depuis l'API
  useEffect(() => {
    const loadExistingArticles = async () => {
      if (!commande?.id) return;

      setLoadingExistingArticles(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // CORRECTION: Pour les acheteurs, charger les articles directement depuis les données déjà présentes
        if (isClientView) {
          console.log('=== CHARGEMENT ARTICLES ACHETEUR ===');
          console.log('Commande complète:', commande);
          console.log('Commandes dans la commande:', commande.commandes);

          // NOUVEAU: Vérifier s'il y a des articles dans les commandes pour le rang actuel
          if (commande.commandes && commande.commandes.length > 0) {
            // CORRECTION: Trouver la commande correspondant au rang actuel
            const commandeForRang = commande.commandes.find((cmd: any) => parseInt(cmd.rang) === rang);

            console.log('Commande trouvée pour le rang', rang, ':', commandeForRang);

            if (commandeForRang && commandeForRang.articles && commandeForRang.articles.length > 0) {
              console.log('Articles trouvés pour le rang', rang, ':', commandeForRang.articles);

              // CORRECTION: Convertir les articles de l'API au format Article[] avec l'ID du catalogue
              const articlesFormatted: Article[] = commandeForRang.articles.map((item: any) => {
                // Extraire les données du catalogue et de l'article
                const catalogue = item.catalogue;
                const articleData = item.catalogue.article;

                // Générer la référence avec le bon département et lot technique
                const departementId = commande.lot_departement?.departement?.id || '00';
                const lotTechniqueId = commande.lot_technique?.id || '000';
                const code = catalogue.code || '';
                const reference = `${departementId.toString().padStart(2, '0')} ${lotTechniqueId} ${code} ${rang}`;

                // CORRECTION: Extraire le prix unitaire et la quantité
                const prixUnitaire = parseFloat(articleData.prix_unitaire) || 0;
                const quantite = parseInt(articleData.quantity) || 1;

                console.log('=== FORMATAGE ARTICLE ACHETEUR (CORRECTED) ===');
                console.log('Catalogue:', catalogue);
                console.log('Article data:', articleData);
                console.log('Catalogue ID (correct):', catalogue.id); // <-- Le bon ID
                console.log('ID catalogue prix:', articleData.id_catalogue_prix); // <-- L'ancien ID incorrect
                console.log('Prix unitaire:', prixUnitaire);
                console.log('Quantité:', quantite);
                console.log('Référence générée:', reference);

                // CORRECTION: Créer et retourner l'objet Article
                return {
                  id: articleData.id?.toString() || '',
                  catalogueId: catalogue.id?.toString() || '',
                  code: reference,
                  nom: catalogue.nom || 'Article sans nom',
                  description: catalogue.description || '',
                  prix: prixUnitaire,
                  unite: catalogue.unite || 'Unité',
                  quantite: quantite,
                  totalHT: prixUnitaire * quantite,
                  // Ajouter les prix dégressifs si disponibles
                  prixDegressifs: {
                    palier1: {
                      min: 1,
                      max: catalogue.seuil_a || 5,
                      prix: prixUnitaire
                    },
                    palier2: {
                      min: (catalogue.seuil_a || 5) + 1,
                      max: catalogue.seuil_b || 10,
                      prix: prixUnitaire
                    },
                    palier3: {
                      min: (catalogue.seuil_b || 10) + 1,
                      prix: prixUnitaire
                    }
                  }
                };
              });

              console.log('Articles formatés pour acheteur:', articlesFormatted);

              // Vérifier que les catalogueId sont corrects
              articlesFormatted.forEach(art => {
                console.log(`Article "${art.nom}" - ID: ${art.id}, CatalogueID: ${art.catalogueId}`);
              });

              // NOUVEAU: Mettre à jour les états immédiatement
              setExistingArticles(articlesFormatted);
              setSelectedProducts(articlesFormatted);
              setArticlesMode('catalogue'); // NOUVEAU: Passer en mode catalogue automatiquement

              // NOUVEAU: Mettre à jour aussi la structure de la commande pour la cohérence
              setCommande((prev: any) => ({
                ...prev,
                formulaire: {
                  ...prev?.formulaire,
                  sections: [{
                    id: 'catalogue-section',
                    nom: 'Articles du catalogue',
                    articles: articlesFormatted.map(art => ({
                      id: art.id,
                      nom: art.nom,
                      code: art.code,
                      description: art.description || art.nom,
                      prix: art.prix,
                      unite: art.unite,
                      quantite: art.quantite,
                      totalHT: art.totalHT
                    }))
                  }]
                }
              }));

              console.log('Articles chargés et mode défini pour acheteur');
              return; // Sortir après avoir traité les articles
            } else {
              console.log('Aucun article trouvé pour le rang', rang);
            }
          } else {
            console.log('Aucune commande avec articles trouvée');
          }

          // NOUVEAU: Si pas d'articles trouvés, s'assurer que les états sont réinitialisés
          setExistingArticles([]);
          setSelectedProducts([]);
          setArticlesMode('initial');
          return;
        }

        // NOUVEAU: Pour les prestataires, utiliser directement les données de la commande déjà chargées
        if (isPrestataireView) {
          if (commande.articles && commande.articles.length > 0) {
            console.log('Articles prestataires trouvés dans commande:', commande.articles);

            // CORRECTION: Fonction de formatage des articles prestataires avec recalcul des prix dégressifs
            const articlesFormatted: Article[] = commande.articles.map((item: any) => {
              console.log('=== FORMATAGE ARTICLE PRESTATAIRE (RECHARGEMENT) ===');
              console.log('Item complet:', item);
              console.log('catalogue_prix:', item.catalogue_prix);
              console.log('prix_unitaire direct:', item.prix_unitaire);

              // CORRECTION: Extraire le catalogue depuis la bonne structure
              const catalogue = item.catalogue_prix?.catalogue || item.catalogue;
              item = catalogue;
              console.log('Catalogue extrait:', catalogue);

              // CORRECTION: Extraire le prix depuis plusieurs sources possibles
              let prix = 0;

              // 1. Essayer prix_unitaire direct de l'item (prix négocié/modifié)
              if (item.article.prix_unitaire && !isNaN(parseFloat(item.article.prix_unitaire))) {
                prix = parseFloat(item.article.prix_unitaire);
                console.log('Prix trouvé via prix_unitaire direct:', prix);
              }
              // 2. Essayer depuis catalogue_prix.prix_a (prix catalogue par défaut)
              else if (item.catalogue_prix?.prix_a && !isNaN(parseFloat(item.catalogue_prix.prix_a))) {
                prix = parseFloat(item.catalogue_prix.prix_a);
                console.log('Prix trouvé via catalogue_prix.prix_a:', prix);
              }
              // 3. Essayer depuis catalogue_prix directement si c'est un nombre
              else if (item.catalogue_prix && !isNaN(parseFloat(item.catalogue_prix))) {
                prix = parseFloat(item.catalogue_prix);
                console.log('Prix trouvé via catalogue_prix direct:', prix);
              }
              // 4. Fallback: essayer depuis le catalogue
              else if (catalogue?.prix && !isNaN(parseFloat(catalogue.prix))) {
                prix = parseFloat(catalogue.prix);
                console.log('Prix trouvé via catalogue.prix:', prix);
              }

              console.log('Prix final retenu:', prix);

              // CORRECTION: Quantité avec plusieurs sources
              let quantite = 1;
              if (item.article.quantity && !isNaN(parseInt(item.article.quantity))) {
                quantite = parseInt(item.article.quantity);
              } else if (item.quantite && !isNaN(parseInt(item.quantite))) {
                quantite = parseInt(item.quantite);
              }
              console.log('Quantité finale:', quantite);

              // CORRECTION: Générer la référence avec les données disponibles
              const departementId = commande.operation?.lot_departement?.departement?.id?.toString().padStart(2, '0') || '00';
              const lotTechniqueId = commande.operation?.lot_technique?.id?.toString() || '000';
              const code = catalogue?.code || item.id?.toString() || '';
              const rangCode = commande.rang || '1';
              const reference = `${departementId} ${lotTechniqueId} ${code} ${rangCode}`;

              console.log('Référence générée:', reference);

              // NOUVEAU: Créer l'objet article avec prix dégressifs AVANT de calculer le total
              const articleBase = {
                id: item.id?.toString() || '',
                catalogueId: item.id_catalogue_prix?.toString() || item.id?.toString(),
                code: reference,
                nom: catalogue?.nom || 'Article',
                description: catalogue?.description || '',
                prix: prix, // Prix de base (palier 1)
                unite: catalogue?.unite || 'Unité',
                quantite: quantite,
                totalHT: 0, // Sera recalculé avec les prix dégressifs
                // NOUVEAU: Ajouter les prix dégressifs si disponibles
                prixDegressifs: item.catalogue_prix ? {
                  palier1: {
                    min: 1,
                    max: catalogue?.seuil_a || 5,
                    prix: parseFloat(item.catalogue_prix.prix_a) || prix
                  },
                  palier2: {
                    min: (catalogue?.seuil_a || 5) + 1,
                    max: catalogue?.seuil_b || 10,
                    prix: parseFloat(item.catalogue_prix.prix_b) || prix
                  },
                  palier3: {
                    min: (catalogue?.seuil_b || 10) + 1,
                    prix: parseFloat(item.catalogue_prix.prix_c) || prix
                  }
                } : undefined
              };

              // CORRECTION: Recalculer le prix unitaire selon la quantité avec les prix dégressifs
              const prixUnitaireCorrect = ArticlesService.getPrixByQuantity(articleBase, quantite);
              const totalHT = prixUnitaireCorrect * quantite;

              console.log('Prix unitaire après recalcul dégressif:', prixUnitaireCorrect);
              console.log('Total HT calculé:', totalHT);

              // CORRECTION: Article final avec le bon total HT
              const articleFormate = {
                ...articleBase,
                totalHT: totalHT
              };

              console.log('Article formaté final:', articleFormate);
              console.log('===============================');

              return articleFormate;
            });

            console.log('Articles formatés pour prestataire (rechargement):', articlesFormatted);
            setExistingArticles(articlesFormatted);
            setSelectedProducts(articlesFormatted);
            setArticlesMode('catalogue'); // NOUVEAU: Passer en mode catalogue
            return;
          }
          return; // Pas d'appel API supplémentaire pour les prestataires
        }

      } catch (error) {
        console.error('Erreur lors du chargement des articles existants:', error);
      } finally {
        setLoadingExistingArticles(false);
      }
    };

    loadExistingArticles();
  }, [commande?.id, commande?.commandes, rang, isPrestataireView, isClientView, setCommande]); // CORRECTION: Ajouter commande.commandes dans les dépendances

  // Memoiser les calculs pour éviter les re-calculs constants
  const rangInfo = useMemo(() => {
    if (!commande) return null;

    let rangActuel = commande.rang_actuel || 1;
    const rangData = commande[`rang_${rang}`] || {};
    const isTransferred = isClientView ? commande.transferred === true : commande.operation?.transferred === true;

    // NOUVEAU: Pour panier inversé, déterminer le rang actuel en fonction des refus et expirations
    if (isTransferred && isClientView) {
      const commandes = commande.commandes || [];

      // Chercher le premier rang qui n'a pas refusé ni expiré
      for (let r = 1; r <= 3; r++) {
        const cmd = commandes.find((c: any) => parseInt(c.rang) === r);
        if (!cmd || (cmd.id_status !== 8 && cmd.id_status !== 9)) {
          // Ce rang n'a pas refusé/expiré (ou n'existe pas encore), c'est le rang actuel
          rangActuel = r;
          break;
        }
      }

      console.log('Panier inversé - Rang actuel calculé:', rangActuel);
    }

    // CORRECTION: Utiliser directement le statut de l'API sans mapping
    let rangStatus;
    if (isClientView && (commande.status?.nom === 'Brouillon' || commande.status?.nom === 'Brouillon Acheteur')) {
      rangStatus = 'Brouillon';
    } else {
      // Utiliser directement le nom du statut de l'API
      rangStatus = commande.status?.nom || 'En attente';
    }

    return {
      isCurrentRang: rangActuel === rang,
      isCompletedRang: rangActuel > rang,
      isPendingRang: rangActuel < rang,
      rangData,
      rangStatus
    };
  }, [commande, rang, isClientView]);

  // CORRECTION: Extraire rangStatus du rangInfo pour éviter l'erreur d'initialisation
  const rangStatus = rangInfo?.rangStatus || 'Chargement';

  // NOUVEAU: Déterminer si le prestataire peut remplir les articles
  const canPrestataireAddArticles = useMemo(() => {
    if (!isPrestataireView || !commande) return false;

    const status = commande.status?.nom;
    const isTransferred = isClientView ?
      commande.transferred === true :
      commande.operation?.transferred === true;

    // Le prestataire peut ajouter des articles si :
    // 1. L'opération a été transférée (transferred: true)
    // 2. Le statut est "Brouillon prestataire"
    // 3. C'est son rang qui est sollicité
    return isTransferred &&
      (status === 'À chiffrer' || status === 'En attente de decision') &&
      rangInfo?.isCurrentRang;
  }, [isPrestataireView, commande, rangInfo]);

  // Vérifier si des articles existent (catalogue ou créés manuellement)
  const hasArticles = useCallback(() => {
    // MODIFICATION: Inclure les articles existants dans la vérification
    if (existingArticles.length > 0 || selectedProducts.length > 0) {
      return true;
    }
    // Articles créés manuellement
    if (commande?.formulaire?.sections?.some((section: any) => section.articles?.length > 0)) {
      return true;
    }
    // CORRECTION: Articles à remplir par le prestataire = on considère qu'il y a des articles
    if (commande?.formulaire?.articlesParPrestataire || articlesMode === 'prestataire') {
      return true;
    }
    return false;
  }, [selectedProducts, existingArticles, commande, articlesMode]); // CORRECTION: Ajouter articlesMode dans les dépendances

  // NOUVEAU: Fonction pour déterminer s'il y a des articles existants
  const hasExistingArticles = useMemo(() => {
    console.log('=== DEBUG hasExistingArticles ===');
    console.log('existingArticles.length:', existingArticles.length);
    console.log('selectedProducts.length:', selectedProducts.length);
    console.log('articlesMode:', articlesMode);

    const hasArticles = existingArticles.length > 0 || selectedProducts.length > 0;
    console.log('hasArticles calculé:', hasArticles);

    return hasArticles;
  }, [existingArticles, selectedProducts]);


  // NOUVEAU: Fonction pour envoyer l'offre du prestataire
  const handleEnvoyerOffrePrestataire = useCallback(async () => {
    try {
      // Vérifier qu'il y a au moins un article
      if (!hasArticles() || existingArticles.length === 0) {
        alert('Vous devez ajouter au moins un article avant d\'envoyer votre offre.');
        return;
      }

      // NOUVEAU: Vérifier les CGV avant de procéder
      if (!acceptCGV) {
        alert('Vous devez accepter les Conditions Générales avant d\'envoyer votre offre.');
        setIsCGVModalOpen(true);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2] || commande?.id_operation || commande?.operation?.id || commande?.id;

      if (!operationId) {
        throw new Error('ID de l\'opération non trouvé');
      }

      console.log('=== ENVOI OFFRE PRESTATAIRE (ACCEPTATION) ===');
      console.log('Operation ID:', operationId);
      console.log('Articles à envoyer:', existingArticles);
      console.log('CGV acceptées:', acceptCGV);

      // CORRECTION: API pour accepter l'opération avec les articles
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/commande/${operationId}/transfertAcheteur`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Offre acceptée avec succès:', result);

      alert('Votre offre a été envoyée avec succès à l\'acheteur !');

      // Recharger la page pour mettre à jour l'interface
      window.location.reload();

    } catch (error) {
      console.error('Erreur lors de l\'acceptation de l\'offre:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'acceptation de l\'offre';
      alert(`Erreur: ${errorMessage}. Veuillez réessayer.`);
    }
  }, [commande, existingArticles, hasArticles, acceptCGV]);

  // CORRECTION: Fonction pour refuser l'opération transférée
  const handleRefuserOperationTransferee = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2] || commande?.id_operation || commande?.operation?.id || commande?.id;

      if (!operationId) {
        throw new Error('ID de l\'opération non trouvé');
      }

      console.log('=== REFUS OPÉRATION PRESTATAIRE ===');
      console.log('Operation ID:', operationId);

      // CORRECTION: API pour refuser l'opération
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/commande/${operationId}/refuse`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Opération refusée avec succès:', result);

      // Message selon le rang pour informer de la cascade
      if (rang < 3) {
        alert(`Opération refusée.`);
      } else {
        alert('Opération refusée.');
      }

      // Recharger la page
      window.location.reload();

    } catch (error) {
      console.error('Erreur lors du refus de l\'opération:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du refus';
      alert(`Erreur: ${errorMessage}. Veuillez réessayer.`);
    }
  }, [commande, rang]);

  // NOUVEAU: Fonction de validation spécifique à ConsultationBC
  const validateConsultationFormBC = useCallback(() => {
    const newErrors: any = {};
    let hasError = false;

    console.log('=== DEBUG VALIDATION CONSULTATION BC ===');
    console.log('Données du formulaire:', commande?.formulaire);

    // Validation des champs administratifs
    if (!commande?.formulaire?.nomOperation?.trim()) {
      newErrors.nomOperation = "Le nom de l'opération est obligatoire";
      hasError = true;
    }

    if (!commande?.formulaire?.adresse?.trim()) {
      newErrors.adresse = "L'adresse est obligatoire";
      hasError = true;
    }

    if (!commande?.formulaire?.codePostal?.trim()) {
      newErrors.codePostal = "Le code postal est obligatoire";
      hasError = true;
    }

    if (!commande?.formulaire?.ville?.trim()) {
      newErrors.ville = "La ville est obligatoire";
      hasError = true;
    }

    // Validation du descriptif technique
    if (!commande?.formulaire?.besoin?.trim()) {
      newErrors.besoin = "Le cahier des charges est obligatoire";
      hasError = true;
    }

    // Si visite préalable requise, valider les conditions
    if (commande?.formulaire?.visitePreavis && !commande?.formulaire?.conditionVisite?.trim()) {
      newErrors.conditionVisite = "Les conditions de visite sont obligatoires";
      hasError = true;
    }

    console.log('Erreurs trouvées:', newErrors);
    console.log('Has error:', hasError);

    // Mettre à jour les erreurs dans l'état
    if (hasError) {
      setErrors(newErrors);
    } else {
      // Effacer les erreurs si tout est valide
      setErrors({});
    }

    return !hasError;
  }, [commande, setErrors]);

  // NOUVEAU: Fonction pour sauvegarder les données de l'opération BC
  const saveOperationData = useCallback(async (validateRequired: boolean = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      // Validation conditionnelle
      if (validateRequired && !validateConsultationFormBC()) {
        throw new Error('Veuillez remplir tous les champs obligatoires');
      }

      // Récupérer l'ID de l'opération depuis l'URL
      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2];

      if (!operationId) {
        throw new Error('ID de l\'opération non trouvé');
      }

      // S'assurer que les dates sont bien formatées pour l'API
      const formatDateForApi = (dateStr: string | null) => {
        if (!dateStr || dateStr === '') return null;
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return null;
          return date.toISOString().split('T')[0];
        } catch (e) {
          console.warn("Erreur de formatage de date:", e);
          return null;
        }
      };

      const dateDebutConsultation = formatDateForApi(commande.formulaire?.dateDebutConsultation) ||
        new Date().toISOString().split('T')[0]; // Fallback sur aujourd'hui

      const dateFinConsultation = formatDateForApi(commande.formulaire?.dateFinConsultation) ||
        (() => {
          // Fallback: début + 10 jours
          const debut = new Date(commande.formulaire?.dateDebutConsultation || new Date());
          const fin = new Date(debut);
          fin.setDate(debut.getDate() + 10);
          return fin.toISOString().split('T')[0];
        })();

      const dateDebutOperation = formatDateForApi(commande.formulaire?.dateDebutOperation) ||
        (() => {
          // Fallback: fin consultation + 1 jour
          const finConsult = new Date(dateFinConsultation);
          const debutOp = new Date(finConsult);
          debutOp.setDate(finConsult.getDate() + 1);
          return debutOp.toISOString().split('T')[0];
        })();

      const dateFinOperation = formatDateForApi(commande.formulaire?.dateFinOperation) ||
        (() => {
          // Fallback: début opération + délai (30 jours par défaut)
          const debutOp = new Date(dateDebutOperation);
          const finOp = new Date(debutOp);
          const delai = commande.formulaire?.delaisFinOperation || 30;
          finOp.setDate(debutOp.getDate() + delai);
          return finOp.toISOString().split('T')[0];
        })();

      console.log('=== DATES UTILISÉES DEPUIS LES INPUTS ===');
      console.log('Date début consultation (input):', commande.formulaire?.dateDebutConsultation);
      console.log('Date fin consultation (input):', commande.formulaire?.dateFinConsultation);
      console.log('Date début opération (input):', commande.formulaire?.dateDebutOperation);
      console.log('Date fin opération (input):', commande.formulaire?.dateFinOperation);
      console.log('Dates formatées pour API:', {
        date_debut_consultation: dateDebutConsultation,
        date_fin_consultation: dateFinConsultation,
        date_debut_operation: dateDebutOperation,
        date_fin_operation: dateFinOperation
      });


      // CORRECTION: Préparer les données avec le mapping correct pour l'API BC
      const dataToSend = {
        nom: commande.formulaire?.nomOperation?.trim() || '',
        reference: commande.formulaire?.referenceInterne?.trim() || '',
        besoin: commande.formulaire?.besoin?.trim() || '',
        adresse: commande.formulaire?.adresse?.trim() || '',
        code_postal: commande.formulaire?.codePostal?.trim() || '',
        ville: commande.formulaire?.ville?.trim() || '',
        // CORRECTION: D'après l'API, facturation = coordonnees_maitrise_ouvrage (anciennement "Maîtrise d'ouvrage")
        facturation: commande.formulaire?.coordonneesMaitriseDOuvrage?.trim() || '',
        // CORRECTION: coordonnees_maitrise_ouvrage = coordonnees_maitrise (anciennement "Maîtrise")
        coordonnees_maitrise_ouvrage: commande.formulaire?.coordonneesMaitrise?.trim() || '',
        // CORRECTION: Condition de visite avec fallback sur chaîne vide
        condition_visite: commande.formulaire?.conditionVisite?.trim() || '',
        visite: commande.formulaire?.visitePreavis ? "1" : "0",
        // NOUVEAU: Ajouter les champs de date si disponibles
        date_debut_operation: dateDebutOperation,
        date_fin_operation: dateFinOperation,
        // NOUVEAU: Dates de consultation calculées automatiquement
        date_debut_consultation: dateDebutConsultation,
        date_fin_consultation: dateFinConsultation,
        // Inclure explicitement le délai d'exécution attendu par l'API
        delai_execution: commande.formulaire?.delaisFinOperation || 30,
      };

      console.log('Sauvegarde des données BC:', dataToSend);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Gérer les erreurs de validation de l'API
        if (response.status === 422 && errorData.errors) {
          const apiErrors: any = {};
          Object.keys(errorData.errors).forEach(field => {
            const messages = errorData.errors[field];
            if (Array.isArray(messages) && messages.length > 0) {
              // CORRECTION: Mapper les noms de champs API vers les noms utilisés dans le frontend
              const fieldMapping: { [key: string]: string } = {
                'nom': 'nomOperation',
                'reference': 'referenceInterne',
                'besoin': 'besoin',
                'adresse': 'adresse',
                'code_postal': 'codePostal',
                'ville': 'ville',
                'condition_visite': 'conditionVisite',
                'facturation': 'coordonneesMaitriseDOuvrage',
                'coordonnees_maitrise': 'coordonneesMaitrise'
              };
              const frontendField = fieldMapping[field] || field;
              apiErrors[frontendField] = messages[0];
            }
          });
          setErrors(apiErrors);
          throw new Error('Erreurs de validation détectées');
        }

        throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Erreur lors de la sauvegarde');
      }

      return responseData;

    } catch (error) {
      console.error('Erreur lors de la sauvegarde BC:', error);
      throw error;
    }
  }, [commande, validateConsultationFormBC, setErrors]);

  const handleEnvoyerOffreAvecCGV = useCallback(() => {
    // Vérifier d'abord s'il y a des articles
    if (!hasArticles() || existingArticles.length === 0) {
      alert('Vous devez ajouter au moins un article avant d\'envoyer votre offre.');
      return;
    }

    // NOUVEAU: Utiliser la même logique que handleLancer pour les CGV
    if (!acceptCGV) {
      // Si CGV non acceptées, ouvrir le modal des CGV
      setReadyToSendAfterCGV(true);
      setIsCGVModalOpen(true);
    } else {
      // Si CGV déjà acceptées, afficher directement la confirmation
      setIsEnvoyerOffreModalOpen(true);
    }
  }, [acceptCGV, hasArticles, existingArticles]);

  // NOUVEAU: Fonction pour gérer le lancement avec CGV
  const handleLancer = useCallback(async () => {
    // Vérifier qu'il y a au moins un article
    if (!hasArticles()) {
      alert("Vous devez créer au moins un article avant de lancer la consultation.");
      return;
    }

    if (validateConsultationFormBC()) {
      try {
        // Sauvegarder les données avant de continuer
        setIsSaving(true);
        await saveOperationData(true);
        setIsSaving(false);

        // Après la sauvegarde, vérifier si les CGV sont acceptées
        if (!acceptCGV) {
          // Si CGV non acceptées, ouvrir le modal des CGV
          setReadyToLaunchAfterCGV(true);
          setIsCGVModalOpen(true);
        } else {
          // Si CGV déjà acceptées, afficher directement la confirmation
          handleConfirmLaunch();
        }
      } catch (error) {
        setIsSaving(false);
        const errorMessage = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';

        if (errorMessage === 'Erreurs de validation détectées') {
          setTimeout(() => {
            const errorElement = document.querySelector('.error');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        } else {
          alert('Erreur lors de la sauvegarde: ' + errorMessage);
        }
      }
    } else {
      // Afficher les messages d'erreur appropriés
      if (!validateConsultationFormBC()) {
        alert('Veuillez remplir tous les champs obligatoires avant de lancer la consultation.');
      } else if (!hasArticles()) {
        alert('Vous devez créer au moins un article avant de lancer la consultation.');
      }
    }
  }, [hasArticles, validateConsultationFormBC, saveOperationData, acceptCGV]);

  // Charger la liste des responsables
  const loadResponsables = async () => {
    setResponsablesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/acheteurs/signataires`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('Responsables chargés:', result);
        setResponsables(result.data || result.signataires || []);
      } else {
        console.error('Erreur lors du chargement des responsables:', response.status);
        setResponsables([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des responsables:', error);
      setResponsables([]);
    } finally {
      setResponsablesLoading(false);
    }
  };

  const handleNotifyResponsable = async () => {
    // CORRECTION: Sauvegarder d'abord les données (descriptif administratif et financier)
    // avant d'afficher la popup de choix du signataire
    setIsSaving(true);

    try {
      await saveOperationData(true); // Valider les champs requis
      console.log('Sauvegarde réussie avant notification');

      // Ouvrir la modal de sélection seulement après la sauvegarde réussie
      setIsNotifyResponsableModalOpen(true);
      loadResponsables();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';

      // Si c'est une erreur de validation, faire défiler vers le premier champ d'erreur
      if (errorMessage === 'Erreurs de validation détectées' || errorMessage === 'Veuillez remplir tous les champs obligatoires') {
        setTimeout(() => {
          const errorElement = document.querySelector('.error');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmNotifyResponsable = async () => {
    try {
      if (!selectedResponsable) {
        alert('Veuillez sélectionner un responsable');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("Token d'authentification manquant");
      }

      // Récupérer l'ID de l'opération depuis l'URL
      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2];

      if (!operationId) {
        throw new Error("ID de l'opération non trouvé");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/notification-responsable`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            responsable_id: selectedResponsable.id,
            responsable_type: selectedResponsable.type
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Demande envoyée avec succès:', result);
      // recharger la page pour mettre à jour l'interface
      window.location.reload();

      alert(`La demande a été envoyée avec succès à ${result.responsable?.prenom} ${result.responsable?.nom}.`);
      setIsNotifyResponsableModalOpen(false);
      setSelectedResponsable(null);



    } catch (error) {
      console.error('Erreur lors de la demande au responsable:', error);
      alert(`Erreur lors de la demande au responsable: ${(error as Error).message}`);
    }
  };
  // NOUVEAU: Fonction pour confirmer le lancement après CGV
  const handleConfirmLaunch = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, []);

  // NOUVEAU: Fonction pour gérer l'acceptation avec CGV (prestataires)
  const handleAccepterAvecCGV = useCallback(() => {
    if (!acceptCGV) {
      // Si CGV non acceptées, ouvrir le modal des CGV
      setReadyToAcceptAfterCGV(true);
      setIsCGVModalOpen(true);
    } else {
      // Si CGV déjà acceptées, afficher directement la confirmation
      handleConfirmAccept();
    }
  }, [acceptCGV]);

  // NOUVEAU: Fonction pour confirmer l'acceptation après CGV
  const handleConfirmAccept = useCallback(() => {
    setIsAcceptModalOpen(true);
  }, []);

  // NOUVEAU: Fonction pour déterminer si l'interface doit être en lecture seule
  const isReadOnlyMode = useMemo(() => {
    if (!commande) return true;

    const status = commande.status?.nom;
    const statusId = commande.status?.id;
    const isTransferred = isClientView ?
      commande.transferred === true :
      commande.operation?.transferred === true;

    console.log('=== DEBUG isReadOnlyMode ===');
    console.log('Status:', status);
    console.log('Status ID:', statusId);
    console.log('Is client view:', isClientView);
    console.log('Is prestataire view:', isPrestataireView);
    console.log('Is transferred:', isTransferred);

    // NOUVEAU: Si l'opération a été transférée, l'acheteur ne peut plus modifier
    if (isTransferred && isClientView) {
      return true;
    }

    // CORRECTION: Pour les prestataires, vérifier s'ils ont déjà agi
    if (isPrestataireView) {
      // Si l'opération est déjà acceptée, refusée ou expirée, passer en lecture seule
      if (statusId === 11 || statusId === 6 || statusId === 12 ||
        status === 'Accepté' || status === 'Valider' ||
        status === 'Refusé' || status === 'Refuser' ||
        status === 'Expiré' || status === 'Expirer') {
        return true;
      }

      // NOUVEAU: Si le prestataire peut ajouter des articles, permettre l'édition
      if (canPrestataireAddArticles) {
        return false; // Permettre l'édition pour remplir les articles
      }

      // Si c'est en consultation ET que c'est le bon rang, permettre les actions
      if ((status === 'En consultation' ||
        status === `En attente de l'acceptation du rang ${rang}`) &&
        rangInfo?.isCurrentRang) {
        return false;
      }

      return true;
    }

    if (isClientView) {
      // CORRECTION: L'acheteur peut modifier en statuts "Brouillon" et "Brouillon Acheteur"
      if (status === 'Brouillon' || status === 'Brouillon acheteur') {
        console.log('Acheteur peut modifier (Brouillon ou Brouillon Acheteur)');
        return false; // Permettre l'édition
      }

      // Si consultation lancée, passer en lecture seule
      if (status === 'En consultation' ||
        status === 'En attente de l\'acceptation du rang 1' ||
        status === 'En attente de l\'acceptation du rang 2' ||
        status === 'En attente de l\'acceptation du rang 3') {
        return true;
      }
    }

    return true;
  }, [commande, isClientView, isPrestataireView, rangInfo, rang, canPrestataireAddArticles]);

  // NOUVEAU: Fonction pour récupérer le rang depuis les commandes (pour ModalArticles)
  const getRangFromCommandes = useMemo(() => {
    if (commande?.commandes && commande.commandes.length > 0) {
      // Retourner le rang de la première commande
      return commande.commandes[0].rang || 1;
    }
    return rang;
  }, [commande, rang]);

  // CORRECTION: Fonction pour lancer la consultation BC - avec mise à jour d'état
  const handleConfirmLancerConsultation = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      // Récupérer l'ID de l'opération depuis l'URL
      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2];

      if (!operationId) {
        throw new Error('ID de l\'opération non trouvé');
      }

      console.log('=== LANCEMENT CONSULTATION BC ===');
      console.log('Operation ID:', operationId);
      console.log('Rang:', rang);

      // NOUVEAU: Utiliser la route PUT spécifique pour lancer la consultation BC
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/consultation`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          rang: rang
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Consultation BC lancée avec succès:', result);

      // NOUVEAU: Mettre à jour immédiatement l'état local pour afficher la section consultation-info
      setCommande((prev: any) => ({
        ...prev,
        status: {
          ...prev?.status,
          nom: `En attente de l'acceptation du rang ${rang}` // Nouveau statut après lancement
        }
      }));

      // Sauvegarder les données avant de naviguer
      await saveOperationData(false);

      alert(`La consultation a bien été envoyée au prestataire de rang ${rang}.`);

      // Rediriger vers l'onglet analyse ou recharger la page
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('tab', '2'); // Tab analyse
      window.location.href = currentUrl.toString();

    } catch (error) {
      console.error('Erreur lors du lancement de la consultation BC:', error);
      alert(`Erreur lors du lancement de la consultation: ${error.message}`);
    }
  }, [rang, saveOperationData, setCommande]); // CORRECTION: Ajouter setCommande dans les dépendances

  // NOUVEAU: Vérifier si la consultation a été lancée
  const consultationStatus = useMemo(() => {
    if (!commande) return { type: 'none', isVisible: false };

    const status = commande.status?.nom;
    const statusId = commande.status?.id;
    const isTransferred = isClientView ?
      commande.transferred === true :
      commande.operation?.transferred === true;

    const brouillonStatuses = [
      'Brouillon',
      'Brouillon acheteur',
    ];

    // NOUVEAU: Si l'opération a été transférée au prestataire
    if (isTransferred) {
      // CORRECTION: Si "À analyser" après refus, NE PAS afficher "Consultation transférée"
      if (status === 'À analyser' || statusId === 12) {
        const commandes = commande.commandes || [];
        const rang1 = commandes.find((cmd: any) => parseInt(cmd.rang) === 1);

        const rang1Refuse = rang1 && rang1.id_status === 8; // Refusé

        if (rang1Refuse) {
          // Rang 1 a refusé, on est en attente de décision acheteur
          // NE PAS afficher de section "Consultation transférée" car on n'a pas encore lancé le rang 2
          return {
            type: 'none', // CORRECTION: Ne rien afficher
            isVisible: false
          };
        }
      }

      // CORRECTION: Afficher "Consultation transférée" uniquement si un rang est effectivement en cours
      const commandes = commande.commandes || [];
      const rangEnCours = commandes.find((cmd: any) =>
        cmd.id_status === 11 || // À chiffrer
        cmd.id_status === 13    // En attente de décision
      );

      if (rangEnCours) {
        return {
          type: 'transferee',
          isVisible: true,
          showOffreEnvoyee: status === 'En attente de décision de l\'acheteur'
        };
      }

      // Si aucun rang en cours, ne rien afficher
      return {
        type: 'none',
        isVisible: false
      };
    }

    if (brouillonStatuses.includes(status)) {
      return {
        type: 'none',
        isVisible: false
      };
    }

    // CORRECTION: Pour TOUS les autres statuts (après lancement), afficher le message de consultation lancée
    const consultationLanceeStatuses = [
      'En consultation',
      'En attente de l\'acceptation du rang 1',
      'En attente de l\'acceptation du rang 2',
      'En attente de l\'acceptation du rang 3',
      'En attente de votre acceptation',
      'En attente de décision de l\'acheteur',
      'Accepté', 'Valider',
      'Refusé', 'Refuser',
      'Expiré', 'Expirer'
    ];

    if (consultationLanceeStatuses.includes(status) || statusId && [2, 3, 4, 5, 6, 11, 12, 13].includes(statusId)) {
      console.log('Consultation lancée détectée pour statut:', status, 'ID:', statusId);
      return {
        type: 'lancee',
        isVisible: true,
        showOffreEnvoyee: status === 'En attente de décision de l\'acheteur' || statusId === 13
      };
    }

    // Par défaut, ne rien afficher
    return {
      type: 'none',
      isVisible: false
    };
  }, [commande, isClientView, isPrestataireView]);

  // CORRECTION: Données pour la section consultation-info (lancée OU transférée)
  useEffect(() => {
    if (!consultationStatus.isVisible || !commande) return;

    // Chercher dans l'historique l'événement de lancement de consultation
    const consultationEvent = commande?.historique
      ?.filter((h: any) => h.prefix?.toLowerCase().includes('consultation'))
      ?.sort((a: any, b: any) =>
        new Date(b.created_at || b.updated_at).getTime() -
        new Date(a.created_at || a.updated_at).getTime()
      )?.[0];

    const formatedConsultationLancee = formatDate(
      consultationEvent?.created_at ||
      consultationEvent?.updated_at ||
      commande.updated_at ||
      commande.created_at
    );

    const consultationOperateur =
      commande?.operation?.organisme?.nom ||
      commande?.organisme?.nom ||
      'Organisme inconnu';

    // Mettre à jour seulement si les données ont changé
    if (
      commande.date_consultation !== formatedConsultationLancee ||
      commande.operateur !== consultationOperateur ||
      commande.type !== consultationStatus.type
    ) {
      setCommande((prev) => ({
        ...prev,
        date_consultation: formatedConsultationLancee,
        operateur: consultationOperateur,
        type: consultationStatus.type,
      }));

      console.log('COMMANDE mise à jour:', {
        date_consultation: formatedConsultationLancee,
        operateur: consultationOperateur
      });
    }
  }, [consultationStatus, commande]);
  // Callbacks pour éviter les re-créations
  const handleFormDataChange = useCallback((newData: any) => {
    setCommande((prev: any) => ({
      ...prev,
      formulaire: { ...prev?.formulaire, ...newData }
    }));
  }, [setCommande]);

  const handleBesoinChange = useCallback((value: string) => {
    setCommande((prev: any) => ({
      ...prev,
      formulaire: {
        ...prev?.formulaire,
        besoin: value
      }
    }));
  }, [setCommande]);

  const handleArticlesChange = useCallback((newData: any) => {
    setCommande((prev: any) => ({
      ...prev,
      [`rang_${rang}`]: {
        ...prev[`rang_${rang}`],
        articles: newData.sections
      }
    }));
  }, [setCommande, rang]);

  // Fonction pour sauvegarder et quitter - utilise saveOperationData
  // MODIFICATION: Ne pas valider les champs obligatoires lors de la sauvegarde simple
  // La validation reste active pour le lancement de consultation et le transfert au prestataire
  const handleSaveAndQuit = async () => {
    setIsSaving(true);

    try {
      await saveOperationData(false); // Pas de validation des champs obligatoires pour la sauvegarde simple
      console.log('Sauvegarde réussie');
      navigate('/operations');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };


  // Fonction personnalisée pour générer le PDF
  const generatePDF = async () => {
    console.log('>>> Fonction generatePDF déclenchée', commande, user);

    if (!commande || !user) {
      alert('Impossible de générer le PDF : données manquantes');
      return;
    }

    try {
      // On complète les infos manquantes avant génération
      const commandeFinale = {
        ...commande,
        date_consultation:
          commande.date_consultation ||
          formatDate(commande.updated_at || commande.created_at),
        operateur:
          commande.operateur ||
          commande?.operation?.organisme?.nom ||
          commande?.organisme?.nom ||
          'Organisme inconnu',
      };

      const pdfData = {
        ...commandeFinale,
        formulaire: commandeFinale.formulaire || {},
      };

      console.log('PDF DATA utilisée pour génération:', pdfData);

      // Génération du blob PDF
      const blob = await pdf(
        <ConsultationPDF commande={pdfData} presta={rang} user={user} />
      ).toBlob();

      // Téléchargement du PDF
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `consultation-${commande.numero || commande.id || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Erreur lors de la génération du PDF:', error);
      console.error(error.stack);
      alert('Une erreur est survenue lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  // Gérer l'ajout d'articles du catalogue
  const handleAddToCommand = useCallback((articles: any[]) => {
    setSelectedProducts(articles);
    setExistingArticles(articles); // NOUVEAU: Synchroniser avec existingArticles

    // NOUVEAU: Passer en mode catalogue si des articles sont ajoutés
    if (articles.length > 0) {
      setArticlesMode('catalogue');

      const catalogueSection = {
        id: 'catalogue-section',
        nom: 'Articles du catalogue',
        articles: articles.map((article) => ({
          id: article.id,
          nom: article.nom,
          code: article.code,
          description: article.description || article.nom,
          prix: article.prix,
          unite: article.unite,
          quantite: article.quantite,
          totalHT: article.totalHT
        }))
      };

      setCommande((prev: any) => ({
        ...prev,
        formulaire: {
          ...prev?.formulaire,
          sections: [catalogueSection]
        }
      }));
    } else {
      // NOUVEAU: Si aucun article, revenir au mode initial
      setArticlesMode('initial');
      setCommande((prev: any) => ({
        ...prev,
        formulaire: {
          ...prev?.formulaire,
          sections: []
        }
      }));
    }
  }, [setCommande]);

  // NOUVEAU: Fonction pour valider les champs administratifs et techniques requis
  const validateAdminAndTechFields = useCallback(() => {
    const errors: any = {};
    let hasError = false;

    // Validation des champs administratifs
    if (!commande?.formulaire?.nomOperation?.trim()) {
      errors.nomOperation = "Le nom de l'opération est obligatoire";
      hasError = true;
    }

    if (!commande?.formulaire?.adresse?.trim()) {
      errors.adresse = "L'adresse est obligatoire";
      hasError = true;
    }

    if (!commande?.formulaire?.codePostal?.trim()) {
      errors.codePostal = "Le code postal est obligatoire";
      hasError = true;
    }

    if (!commande?.formulaire?.ville?.trim()) {
      errors.ville = "La ville est obligatoire";
      hasError = true;
    }

    // Validation du descriptif technique
    if (!commande?.formulaire?.besoin?.trim()) {
      errors.besoin = "Le cahier des charges est obligatoire";
      hasError = true;
    }

    // Si visite préalable requise, valider les conditions
    if (commande?.formulaire?.visitePreavis && !commande?.formulaire?.conditionVisite?.trim()) {
      errors.conditionVisite = "Les conditions de visite sont obligatoires";
      hasError = true;
    }

    // Mettre à jour les erreurs dans l'état
    if (hasError) {
      setErrors(errors);
    }

    return !hasError;
  }, [commande, setErrors]);

  // CORRECTION: Fonction pour transférer au prestataire via API
  const handleTransfertPrestataire = useCallback(async () => {
    try {
      // NOUVEAU: Valider les champs requis avant de procéder au transfert
      if (!validateAdminAndTechFields()) {
        alert('Vous devez d\'abord compléter le descriptif administratif et technique avant de procéder au transfert de l\'opération.');

        // NOUVEAU: Faire défiler vers le premier champ d'erreur
        setTimeout(() => {
          const errorElement = document.querySelector('.error, .error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);

        return; // Arrêter l'exécution si la validation échoue
      }

      // NOUVEAU: Sauvegarder les données avant de procéder au transfert
      try {
        await saveOperationData(true); // Validation stricte avant transfert
      } catch (saveError) {
        console.error('Erreur lors de la sauvegarde avant transfert:', saveError);
        const errorMessage = saveError instanceof Error ? saveError.message : 'Erreur lors de la sauvegarde';

        if (errorMessage === 'Erreurs de validation détectées') {
          alert('Vous devez d\'abord compléter le descriptif administratif et technique avant de procéder au transfert de l\'opération.');
          // Faire défiler vers le premier champ d'erreur
          setTimeout(() => {
            const errorElement = document.querySelector('.error, .error-message');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        } else {
          alert(`Erreur lors de la sauvegarde: ${errorMessage}`);
        }
        return; // Arrêter l'exécution si la sauvegarde échoue
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      console.log('Transfert au prestataire via API...');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/operation/${commande.id}/transfertPrestataire`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Transfert au prestataire réussi:', result);

      // NOUVEAU: Mettre à jour la commande avec la réponse de l'API (qui contient transferred: true)
      if (result.data) {
        // Merge previous formulaire to avoid losing unsaved form inputs if API response omits them
        setCommande((prev: any) => ({
          ...prev,
          ...result.data,
          formulaire: {
            ...prev?.formulaire,
            ...result.data.formulaire
          }
        }));
      } else {
        // Fallback: mettre à jour manuellement si l'API ne retourne pas les données complètes
        setCommande((prev: any) => ({
          ...prev,
          transferred: true,
          updated_at: new Date().toISOString()
        }));
      }

      alert('Demande envoyée au prestataire avec succès. Il définira les articles nécessaires.');


      window.location.reload();

    } catch (error) {
      console.error('Erreur lors du transfert au prestataire:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du transfert au prestataire';
      alert(`Erreur: ${errorMessage}. Veuillez réessayer.`);
    }
  }, [commande?.id, setCommande, validateAdminAndTechFields, saveOperationData]);

  // CORRECTION: Modifier handleConfirmModeChange pour utiliser la nouvelle API
  const handleConfirmModeChange = useCallback(async () => {
    setIsChangeModeModalOpen(false);

    // CORRECTION: Utiliser la nouvelle API de transfert
    await handleTransfertPrestataire();
  }, [handleTransfertPrestataire]);

  // Fonction pour rouvrir le modal avec les articles existants
  const handleReopenArticlesModal = useCallback(() => {
    // Préparer les articles existants pour le modal
    const existingArticles = selectedProducts.map(product => ({
      ...product,
      quantite: product.quantite || 1,
      totalHT: (product.prix || 0) * (product.quantite || 1)
    }));

    // Ouvrir le modal
    setIsArticlesModalOpen(true);
  }, [selectedProducts]);

  // Mise à jour de la fonction handleRemoveProduct pour plus de feedback
  const handleRemoveProduct = useCallback((productId: string) => {
    const productToRemove = selectedProducts.find(p => p.id === productId);

    if (productToRemove && window.confirm(`Êtes-vous sûr de vouloir supprimer "${productToRemove.nom}" ?`)) {
      const updatedProducts = selectedProducts.filter(p => p.id !== productId);
      handleAddToCommand(updatedProducts);
    }
  }, [selectedProducts, handleAddToCommand]);

  // CORRECTION: Ajouter la fonction handleQuantityChange manquante
  const handleQuantityChange = useCallback((index: number, newQuantity: number) => {
    const updatedProducts = selectedProducts.map((product, i) => {
      if (i === index) {
        const quantity = Math.max(1, newQuantity);
        const prixUnitaire = ArticlesService.getPrixByQuantity(product, quantity);
        return {
          ...product,
          quantite: quantity,
          totalHT: prixUnitaire * quantity
        };
      }
      return product;
    });

    setSelectedProducts(updatedProducts);
    handleAddToCommand(updatedProducts);
  }, [selectedProducts, handleAddToCommand]);

  // NOUVEAU: Fonction pour modifier la quantité des articles existants
  const handleExistingQuantityChange = useCallback(async (index: number, quantity: number) => {
    const validQuantity = Math.max(1, quantity);
    const article = existingArticles[index];

    if (!article) return;

    // CORRECTION: Mettre à jour immédiatement l'interface avec recalcul des prix
    const updatedArticles = existingArticles.map((art, i) => {
      if (i === index) {
        // CORRECTION: Recalculer le prix unitaire avec les prix dégressifs
        const prixUnitaire = ArticlesService.getPrixByQuantity(art, validQuantity);
        const totalHT = prixUnitaire * validQuantity;

        console.log('=== RECALCUL PRIX LORS CHANGEMENT QUANTITÉ ===');
        console.log('Article:', art.nom);
        console.log('Ancienne quantité:', art.quantite);
        console.log('Nouvelle quantité:', validQuantity);
        console.log('Prix unitaire recalculé:', prixUnitaire);
        console.log('Total HT recalculé:', totalHT);

        return {
          ...art,
          quantite: validQuantity,
          totalHT: totalHT
        };
      }
      return art;
    });

    setExistingArticles(updatedArticles);
    setSelectedProducts(updatedArticles);

    // NOUVEAU: Sauvegarder automatiquement en BDD avec détection du contexte
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      // NOUVEAU: Déterminer le contexte (prestataire vs acheteur)
      const isPrestataire = (() => {
        // 1. Vérifier si c'est un prestataire avec opération transférée
        const isTransferred = isClientView ?
          commande?.transferred === true :
          commande?.operation?.transferred === true;

        if (isPrestataireView && isTransferred) {
          console.log('Contexte prestataire transféré détecté');
          return true;
        }

        // 2. Vérifier l'URL - si elle contient 'bon-commande' ET que ce n'est pas dans /operation-ms/
        const urlContainsBonCommande = window.location.pathname.includes('bon-commande');
        const urlNotInOperations = !window.location.pathname.includes('operations');
        if (urlContainsBonCommande && urlNotInOperations) {
          console.log('Contexte prestataire détecté via URL bon-commande');
          return true;
        }

        console.log('Contexte acheteur détecté (défaut)');
        return false;
      })();

      const catalogueId = article.catalogueId || article.id;

      // CORRECTION: Extraire l'ID selon le contexte
      let entityId;
      if (isPrestataire) {
        // Pour les prestataires, utiliser l'ID de la commande
        const pathParts = window.location.pathname.split('/');
        const bonCommandeIndex = pathParts.indexOf('bon-commande');
        if (bonCommandeIndex !== -1 && pathParts[bonCommandeIndex + 1]) {
          entityId = pathParts[bonCommandeIndex + 1];
        } else {
          entityId = commande?.id;
        }
      } else {
        // Pour les acheteurs, utiliser l'ID de l'opération
        entityId = commande?.id;
      }

      // CORRECTION: Nouveau payload pour la route PUT
      const payload = {
        quantity: validQuantity.toString()
      };

      console.log('Sauvegarde automatique de la quantité:', {
        contextePrestataire: isPrestataire,
        articleModifie: article.nom,
        entityId: entityId,
        catalogueId: catalogueId,
        nouvelleQuantite: validQuantity,
        payload: payload
      });

      // CORRECTION: Utiliser l'endpoint approprié selon le contexte
      const endpoint = isPrestataire
        ? `/bdc/commande/${entityId}/catalogue/${catalogueId}`
        : `/bdc/operation/${entityId}/catalogue/${catalogueId}`;

      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erreur lors de la sauvegarde automatique:', errorData);
        // Ne pas afficher d'erreur à l'utilisateur pour la sauvegarde automatique
      } else {
        console.log('Quantité sauvegardée automatiquement');
      }

    } catch (error) {
      console.error('Erreur lors de la sauvegarde automatique de la quantité:', error);
      // Ne pas afficher d'erreur à l'utilisateur pour la sauvegarde automatique
    }

    // Synchroniser avec la commande
    setCommande((prev: any) => ({
      ...prev,
      formulaire: {
        ...prev?.formulaire,
        sections: [{
          id: 'catalogue-section',
          nom: 'Articles du catalogue',
          articles: updatedArticles.map(art => ({
            id: art.id,
            nom: art.nom,
            code: art.code,
            description: art.description || art.nom,
            prix: art.prix,
            unite: art.unite,
            quantite: art.quantite,
            totalHT: art.totalHT
          }))
        }]
      }
    }));
  }, [existingArticles, commande, isClientView, isPrestataireView, setCommande]);

  // NOUVEAU: Fonction pour supprimer un article existant
  const handleRemoveExistingArticle = useCallback(async (catalogueId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;

    // NOUVEAU: Trouver l'article à supprimer pour affichage
    const articleToRemove = existingArticles.find(article =>
      article.catalogueId === catalogueId || article.id === catalogueId
    );

    console.log('=== DEBUG SUPPRESSION ARTICLE ===');
    console.log('ID à supprimer:', catalogueId);
    console.log('Article trouvé:', articleToRemove);
    console.log('Articles avant suppression:', existingArticles);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      // NOUVEAU: Déterminer le contexte (prestataire vs acheteur)
      const isPrestataire = (() => {
        // 1. Vérifier si c'est un prestataire avec opération transférée
        const isTransferred = isClientView ?
          commande?.transferred === true :
          commande?.operation?.transferred === true;

        if (isPrestataireView && isTransferred) {
          console.log('Contexte prestataire transféré détecté');
          return true;
        }

        // 2. Vérifier l'URL - si elle contient 'bon-commande' ET que ce n'est pas dans /operation-ms/
        const urlContainsBonCommande = window.location.pathname.includes('bon-commande');
        const urlNotInOperations = !window.location.pathname.includes('operations');
        if (urlContainsBonCommande && urlNotInOperations) {
          console.log('Contexte prestataire détecté via URL bon-commande');
          return true;
        }

        console.log('Contexte acheteur détecté (défaut)');
        return false;
      })();

      // CORRECTION: Extraire l'ID selon le contexte
      let entityId;
      if (isPrestataire) {
        // Pour les prestataires, utiliser l'ID de la commande
        const pathParts = window.location.pathname.split('/');
        const bonCommandeIndex = pathParts.indexOf('bon-commande');
        if (bonCommandeIndex !== -1 && pathParts[bonCommandeIndex + 1]) {
          entityId = pathParts[bonCommandeIndex + 1];
        } else {
          entityId = commande?.id;
        }
        console.log('ID commande prestataire pour suppression:', entityId);
      } else {
        // Pour les acheteurs, utiliser l'ID de l'opération
        entityId = commande?.id;
        console.log('ID opération acheteur pour suppression:', entityId);
      }

      if (!entityId) {
        throw new Error('ID de l\'entité non trouvé');
      }

      // CORRECTION: Utiliser l'endpoint approprié selon le contexte
      const endpoint = isPrestataire
        ? `/bdc/commande/${entityId}/catalogue/${catalogueId}`
        : `/bdc/operation/${entityId}/catalogue/${catalogueId}`;

      console.log('=== APPEL API SUPPRESSION ===');
      console.log('Contexte prestataire:', isPrestataire);
      console.log('Entity ID:', entityId);
      console.log('Catalogue ID:', catalogueId);
      console.log('Endpoint utilisé:', endpoint);
      console.log('URL complète:', `${process.env.REACT_APP_API_URL}${endpoint}`);

      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Réponse API:', {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText
      });

      if (response.ok) {
        console.log('=== SUPPRESSION API RÉUSSIE ===');

        // CORRECTION: Supprimer l'article des états locaux en utilisant catalogueId OU id
        const updatedArticles = existingArticles.filter(article => {
          const shouldKeep = article.catalogueId !== catalogueId && article.id !== catalogueId;
          console.log(`Article ${article.nom} (catalogueId: ${article.catalogueId}, id: ${article.id}) - Garder: ${shouldKeep}`);
          return shouldKeep;
        });

        console.log('Articles après filtrage:', updatedArticles);
        console.log('Nombre d\'articles avant:', existingArticles.length);
        console.log('Nombre d\'articles après:', updatedArticles.length);

        // NOUVEAU: Mettre à jour immédiatement les états
        setExistingArticles(updatedArticles);
        setSelectedProducts(updatedArticles);

        // NOUVEAU: Si plus d'articles, revenir au mode initial
        if (updatedArticles.length === 0) {
          console.log('Plus d\'articles - retour au mode initial');
          setArticlesMode('initial');
          // NOUVEAU: Nettoyer aussi les sections dans la commande
          setCommande((prev: any) => ({
            ...prev,
            formulaire: {
              ...prev?.formulaire,
              sections: []
            }
          }));
        } else {
          // NOUVEAU: Mettre à jour la commande avec les articles restants
          setCommande((prev: any) => ({
            ...prev,
            formulaire: {
              ...prev?.formulaire,
              sections: [{
                id: 'catalogue-section',
                nom: 'Articles du catalogue',
                articles: updatedArticles.map(art => ({
                  id: art.id,
                  nom: art.nom,
                  code: art.code,
                  description: art.description || art.nom,
                  prix: art.prix,
                  unite: art.unite,
                  quantite: art.quantite,
                  totalHT: art.totalHT
                }))
              }]
            }
          }));
        }

        // Message de confirmation
        const articleName = articleToRemove?.nom || 'Article';
        console.log(`✅ ${articleName} supprimé avec succès`);

        // OPTIONNEL: Toast au lieu d'une alerte
        // alert('Article supprimé avec succès');

      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erreur API:', errorData);
        throw new Error(errorData.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'article:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la suppression de l\'article';
      alert(`Erreur: ${errorMessage}`);

      // En cas d'erreur, ne pas modifier les états locaux pour garder la cohérence
    }
  }, [commande, existingArticles, isClientView, isPrestataireView, setCommande]);

  // ... (garder le reste du code jusqu'au return)

  if (!rangInfo) {
    return <div>Chargement...</div>;
  }

  const { isCurrentRang, isCompletedRang, isPendingRang, rangData } = rangInfo;
  // CORRECTION: Ne plus extraire rangStatus ici car il est déjà défini plus haut

  const getHeaderTitle = () => {
    switch (rang) {
      case 1: return 'Consultation Prestataire Rang 1';
      case 2: return 'Consultation Prestataire Rang 2';
      case 3: return 'Consultation Prestataire Rang 3';
      default: return 'Consultation';
    }
  };

  const getStatusInfo = () => {
    if (isCompletedRang) {
      return {
        message: `Le prestataire de rang ${rang} a ${rangData.accepted ? 'accepté' : 'refusé'} la commande`,
        color: rangData.accepted ? '#00A708' : '#D90E0E'
      };
    } else if (isPendingRang) {
      return {
        message: `En attente du traitement des rangs précédents`,
        color: '#6B7280'
      };
    } else if (isCurrentRang) {
      if (rangStatus === 'En consultation') {
        return {
          message: `Consultation en cours avec le prestataire de rang ${rang}`,
          color: '#F4751B'
        };
      } else if (rangStatus === 'Brouillon') {
        return {
          message: `Prêt à consulter le prestataire de rang ${rang}`,
          color: '#6B7280'
        };
      }
    }
    return null;
  };

  // NOUVEAU: Fonction pour accepter une commande côté prestataire avec la bonne API
  const handleAccepterCommandePrestataire = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      // Récupérer l'ID de l'opération depuis l'URL ou depuis les données de la commande
      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2] || commande?.id_operation || commande?.operation?.id;

      if (!operationId) {
        throw new Error('ID de l\'opération non trouvé');
      }

      console.log('=== ACCEPTATION COMMANDE PRESTATAIRE ===');
      console.log('Operation ID:', operationId);
      console.log('Commande:', commande);

      // NOUVEAU: Utiliser la nouvelle API pour accepter une commande
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/commande/${operationId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Commande acceptée avec succès:', result);

      alert('Opération acceptée avec succès !');

      // Recharger la page pour mettre à jour l'interface
      window.location.reload();

    } catch (error) {
      console.error('Erreur lors de l\'acceptation de la commande:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'acceptation';
      alert(`Erreur: ${errorMessage}. Veuillez réessayer.`);
    }
  }, [commande]);

  // NOUVEAU: Fonction pour refuser une commande côté prestataire avec la bonne API
  const handleRefuserCommandePrestataire = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      // Récupérer l'ID de l'opération depuis l'URL ou depuis les données de la commande
      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2] || commande?.id_operation || commande?.operation?.id;

      if (!operationId) {
        throw new Error('ID de l\'opération non trouvé');
      }

      console.log('=== REFUS COMMANDE PRESTATAIRE ===');
      console.log('Operation ID:', operationId);
      console.log('Commande:', commande);

      // NOUVEAU: Utiliser la nouvelle API pour refuser une commande
      const response = await fetch(`${process.env.REACT_APP_API_URL}/commande/${operationId}/refuse`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Commande refusée avec succès:', result);

      // Message différent selon le rang pour informer de la cascade
      if (rang < 3) {
        alert(`Opération refusée.`);
      } else {
        alert('Opération refusée.');
      }

      // Recharger la page pour mettre à jour l'interface
      window.location.reload();

    } catch (error) {
      console.error('Erreur lors du refus de la commande:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du refus';
      alert(`Erreur: ${errorMessage}. Veuillez réessayer.`);
    }
  }, [commande, rang]);

  const statusInfo = getStatusInfo();

  // CORRECTION: Fonction pour déterminer si une section spécifique doit être en lecture seule
  const isSectionReadOnly = useCallback((section: 'admin' | 'technique' | 'financier') => {
    if (!commande) return true;

    // NOUVEAU: Si expiré, tout est en lecture seule
    if (isOperationExpired) return true;

    const status = commande.status?.nom;
    const statusId = commande.status?.id;
    const isTransferred = isClientView ?
      commande.transferred === true :
      commande.operation?.transferred === true;

    console.log('=== DEBUG isSectionReadOnly ===');
    console.log('Section:', section);
    console.log('Status:', status);
    console.log('Status ID:', statusId);
    console.log('Is client view:', isClientView);
    console.log('Is prestataire view:', isPrestataireView);
    console.log('Is transferred:', isTransferred);
    console.log('Rang status:', rangStatus);

    // NOUVEAU: Si le prestataire a déjà accepté ou refusé, TOUT passe en lecture seule
    if (isPrestataireView) {
      if (
        status === 'Accepté' || status === 'Valider' ||
        status === 'Refusé' || status === 'Refuser' ||
        status === 'Expiré' || status === 'Expirer' ||
        status === 'En attente de décision de l\'acheteur') {
        return true; // TOUTES les sections en lecture seule après action du prestataire
      }

      // Pour les prestataires avec opération transférée (encore en cours)
      if (isTransferred && canPrestataireAddArticles) {
        switch (section) {
          case 'admin':
          case 'technique':
            // Descriptifs administratif et technique en lecture seule pour prestataires transférés
            return true;
          case 'financier':
            // Seul le descriptif financier est modifiable pour les prestataires transférés (en cours)
            return false;
          default:
            return true;
        }
      }

      // Si c'est en consultation ET que c'est le bon rang, permettre les actions
      if ((status === 'En consultation' ||
        status === `En attente de l'acceptation du rang ${rang}`) &&
        rangInfo?.isCurrentRang) {
        return false;
      }

      return true;
    }

    if (isClientView) {
      // CORRECTION: L'acheteur peut modifier en statuts "Brouillon" et "Brouillon Acheteur"
      if (status === 'Brouillon' || status === 'Brouillon acheteur') {
        console.log('Acheteur peut modifier (Brouillon ou Brouillon Acheteur)');
        return false; // Permettre l'édition
      }

      // Si consultation lancée, passer en lecture seule
      if (status === 'En consultation' ||
        status === 'En attente de l\'acceptation du rang 1' ||
        status === 'En attente de l\'acceptation du rang 2' ||
        status === 'En attente de l\'acceptation du rang 3') {
        return true;
      }
    }

    return true;
  }, [commande, isClientView, isPrestataireView, canPrestataireAddArticles, isReadOnlyMode]);

  // Fonction pour envoyer pour validation
  const handleEnvoyerPourValidation = useCallback(async () => {
    if (!selectedValidateur) {
      alert('Veuillez sélectionner un validateur avant d\'envoyer pour validation.');
      return;
    }

    try {
      // Sauvegarder d'abord les données
      await saveOperationData(true);

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/operation/${commande.id}/envoyer-validation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id_validateur: selectedValidateur
        })
      });

      if (response.ok) {
        alert('Opération envoyée pour validation avec succès !');
        window.location.reload();
      } else {
        throw new Error('Erreur lors de l\'envoi pour validation');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'envoi pour validation');
    }
  }, [selectedValidateur, commande?.id, saveOperationData]);

  // Modal pour confirmer l'envoi au prestataire
  const ModalEnvoyerPrestataire = () => (
    <ValidationPopUp
      isOpen={isEnvoyerPrestataireModalOpen}
      title="Envoyer une demande au prestataire"
      text={`Vous vous apprêtez à envoyer une demande au prestataire de Rang ${rang} pour qu'il remplisse les articles pour vous. Attention, vous ne pourrez plus revenir sur le descriptif Administratif et Technique. Cette action est irréversible.`}
      onClose={() => setIsEnvoyerPrestataireModalOpen(false)}
      onConfirm={async () => {
        // NOUVEAU: Vérifier les CGV avant de procéder
        if (!acceptCGV) {
          // Fermer le modal actuel, marquer qu'on est prêt au transfert, et ouvrir le modal CGV
          setIsEnvoyerPrestataireModalOpen(false);
          setReadyToTransfertAfterCGV(true);
          setIsCGVModalOpen(true);
          return;
        }

        setIsEnvoyerPrestataireModalOpen(false);

        // NOUVEAU: La validation se fait maintenant dans handleTransfertPrestataire
        setArticlesMode('prestataire');

        try {
          await handleTransfertPrestataire();
          console.log('Demande envoyée au prestataire avec succès');
        } catch (error) {
          console.error('Erreur d\'envoi au prestataire:', error);
          // Ne pas afficher d'alert ici car handleTransfertPrestataire gère déjà les erreurs
          // Remettre le mode initial si le transfert échoue
          setArticlesMode('initial');
        }
      }}
      cancelLabel="Annuler"
      confirmLabel="Valider"
    />
  );

  // CORRECTION: Modal pour confirmer l'envoi de l'offre (acceptation)
  const ModalEnvoyerOffre = () => (
    <ValidationPopUp
      isOpen={isEnvoyerOffreModalOpen}
      title="Accepter et envoyer votre offre"
      text={`Êtes-vous sûr de vouloir accepter cette opération et envoyer votre offre avec ${existingArticles.length} article(s) à l'acheteur ? Cette action est définitive.`}
      onClose={() => setIsEnvoyerOffreModalOpen(false)}
      onConfirm={async () => {
        setIsEnvoyerOffreModalOpen(false);
        await handleEnvoyerOffrePrestataire();
      }}
      cancelLabel="Annuler"
      confirmLabel="Accepter et envoyer"
    />
  );

  // CORRECTION: Modal pour confirmer le refus de l'opération
  const ModalRefuserTransfert = () => (
    <ValidationPopUp
      isOpen={isRefuserTransfertModalOpen}
      title="Refuser cette opération"
      text={`Êtes-vous sûr de vouloir refuser cette opération ? ${rang < 3 ? 'Elle sera automatiquement transmise au prestataire suivant.' : 'Cette opération sera définitivement refusée.'}`}
      onClose={() => setIsRefuserTransfertModalOpen(false)}
      onConfirm={async () => {
        setIsRefuserTransfertModalOpen(false);
        await handleRefuserOperationTransferee();
      }}
      cancelLabel="Annuler"
      confirmLabel="Refuser l'opération"
    />
  );

  // Modal pour confirmer l'acceptation pour prestataire
  const ModalAccepterCommande = () => (
    <ValidationPopUp
      isOpen={isAcceptModalOpen}
      title="Accepter l'opération"
      text="Êtes-vous sûr de vouloir accepter cette opération ?"
      onClose={() => setIsAcceptModalOpen(false)}
      onConfirm={async () => {
        setIsAcceptModalOpen(false);
        // CORRECTION: Utiliser la nouvelle fonction API
        await handleAccepterCommandePrestataire();
      }}
      cancelLabel="Non"
      confirmLabel="Oui"
    />
  );

  // Modal refuser pour prestataire
  const ModalRefuserCommande = () => (
    <ValidationPopUp
      isOpen={isRefuseModalOpen}
      title="Refuser l'opération"
      text="Êtes-vous sûr de vouloir refuser cette opération ?"
      onClose={() => setIsRefuseModalOpen(false)}
      onConfirm={async () => {
        setIsRefuseModalOpen(false);
        // CORRECTION: Utiliser la nouvelle fonction API
        await handleRefuserCommandePrestataire();
      }}
      cancelLabel="Non"
      confirmLabel="Oui"
    />
  );

  // Modal pour notifier le responsable
  const ModalNotifyResponsable = () => (
    <ValidationPopUp
      isOpen={isNotifyResponsableModalOpen}
      title="Obtenir la validation de l'acheteur signataire"
      text=""
      onClose={() => {
        setIsNotifyResponsableModalOpen(false);
        setSelectedResponsable(null);
      }}
      onConfirm={handleConfirmNotifyResponsable}
      cancelLabel="Annuler"
      confirmLabel="Envoyer la demande de validation"
    >
      <div className="mt-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sélectionnez le responsable à qui envoyer la demande :
        </label>
        {responsablesLoading ? (
          <p className="text-gray-500">Chargement...</p>
        ) : responsables.length === 0 ? (
          <p className="text-red-500">Aucun responsable disponible</p>
        ) : (
          <select
            style={{ marginTop: '16px', marginBottom: '24px' }}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={selectedResponsable ? `${selectedResponsable.type}-${selectedResponsable.id}` : ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                const [type, id] = value.split('-');
                setSelectedResponsable({ id: parseInt(id), type });
              } else {
                setSelectedResponsable(null);
              }
            }}
          >
            <option value="">-- Choisir un responsable --</option>
            {responsables.map((resp: any) => (
              <option
                key={`${resp.type}-${resp.id}`}
                value={`${resp.type}-${resp.id}`}
              >
                {`${resp.prenom.charAt(0).toUpperCase() + resp.prenom.slice(1).toLowerCase()} ${resp.nom.toUpperCase()}`} - {resp.type === 'responsable_achat' ? 'Administrateur' : 'Signataire'}{resp.fonction ? ` (${resp.fonction})` : ''}              </option>
            ))}
          </select>
        )}
        {selectedResponsable && (
          <p className="mt-2 text-sm text-gray-600">
            Email : {responsables.find((r: any) => r.id === selectedResponsable.id && r.type === selectedResponsable.type)?.email_contact}
          </p>
        )}
      </div>
    </ValidationPopUp>
  );

  const ModalChangerMode = () => (
    <ValidationPopUp
      isOpen={isChangeModeModalOpen}
      title="Laisser le prestataire remplir les articles"
      text="Attention, en changeant de mode, tous vos articles seront supprimés et remplacés par ceux du prestataire. Cette action est irréversible."
      onClose={() => setIsChangeModeModalOpen(false)}
      onConfirm={handleConfirmModeChange}
      cancelLabel="Annuler"
      confirmLabel="Confirmer"
    />
  );

  // NOUVEAU: Log pour débugger l'affichage
  console.log('=== DEBUG AFFICHAGE MODE PRESTATAIRE ===', {
    articlesMode,
    articlesParPrestataire: commande?.formulaire?.articlesParPrestataire,
    demandeEnvoyeePrestataire: commande?.formulaire?.demandeEnvoyeePrestataire,
    isReadOnlyMode,
    hasExistingArticles,
    isClientView,
    isPrestataireView,
    rangStatus
  });

  const getDisplayStatus = () => {
    if (!commande?.status) return 'Statut inconnu';

    const statusName = commande.status.nom;
    const statusId = commande.status.id;

    // Pour les prestataires BC, adapter l'affichage selon le contexte
    if (isPrestataireView) {
      const rang = getRangPrestataire();

      // Si c'est "En attente de l'acceptation du rang X" et que c'est le rang du prestataire connecté
      if (statusName === `En attente de l'acceptation du rang ${rang}`) {
        return 'À accepter';
      }
      // Sinon, utiliser le nom tel quel
      return statusName;
    }

    // Pour les acheteurs, utiliser le nom tel quel
    return statusName;
  };

  // CORRECTION: Déterminer le rang du prestataire depuis les données API
  const getRangPrestataire = () => {
    if (!commande || !isPrestataireView) return 1;
    return commande.rang || 1;
  };

  // CORRECTION: Fonction pour formater le sous-titre
  const getSousTitre = () => {
    const numero = commande?.operation?.numero || commande?.numero;
    const rang = getRangPrestataire();

    if (isPrestataireView) {
      return `Suivi de la Commande BC n° ${numero} - Rang ${rang}`;
    }
    return `Suivi de la Commande BC n° ${numero}`;
  };

  // CORRECTION: Fonction pour formater le subtitle (lot technique + géographique)
  const getSubtitle = () => {
    const lotTechnique = commande?.operation?.lot_technique || commande?.lot_technique;
    const departement = commande?.operation?.lot_departement?.departement || commande?.lot_departement?.departement;

    if (lotTechnique && departement) {
      return `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`;
    }
    return 'Informations de lot non disponibles';
  };

  // NOUVEAU: Enrichir formData avec delaisFinOperation calculé depuis l'API si absent
  const enrichedFormData = useMemo(() => {
    const baseFormData = commande?.formulaire || {};

    // Si delaisFinOperation est déjà présent, le garder
    if (baseFormData.delaisFinOperation) {
      return { ...baseFormData, delaisConsultation: 10 };
    }

    // Sinon, essayer de le calculer/récupérer
    let delaisFinOperation = 30; // Valeur par défaut

    // 1. Essayer depuis l'API (delai_execution)
    if (commande?.delai_execution) {
      delaisFinOperation = commande.delai_execution;
    }
    // 2. Calculer depuis les dates si disponibles
    else if (commande?.date_debut_operation && commande?.date_fin_operation) {
      const debut = new Date(commande.date_debut_operation);
      const fin = new Date(commande.date_fin_operation);
      const diffTime = Math.abs(fin.getTime() - debut.getTime());
      delaisFinOperation = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    return {
      ...baseFormData,
      delaisConsultation: 10,
      delaisFinOperation
    };
  }, [commande]);

  return (
    <>
      <div className="consultation-bc">

        {isOperationExpired && (
          <div className="expired-notice" style={{
            backgroundColor: '#fee',
            border: '2px solid #c33',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#c33',
            fontWeight: 'bold',
            textAlign: 'center'
          }}>
            Cette commande est expirée. {isPrestataireView ? 'Vous ne pouvez plus accepter ou refuser cette consultation.' : 'Vous ne pouvez plus modifier cette consultation.'}
          </div>
        )}

        {/* Descriptif Administratif */}
        <section>
          <h3 className="section-header">Descriptif Administratif</h3>
          <div className="section-content">
            <FormulaireAdministratif
              formData={enrichedFormData}
              viewMode={isSectionReadOnly('admin')} // CORRECTION: Utiliser la nouvelle fonction spécifique
              hideBesoin={true}
              errors={errors}
              commande={commande}
              operation={commande}
              setFormData={!isSectionReadOnly('admin') ? handleFormDataChange : undefined} // CORRECTION: Conditionnel sur la section
            />
          </div>
        </section>

        {/* Descriptif Technique */}
        <section>
          <h3 className="section-header">Descriptif Technique</h3>
          <div className="section-content">
            {!isSectionReadOnly('technique') ? ( // CORRECTION: Utiliser la nouvelle fonction spécifique
              <div className="form-group">
                <label>Cahier des charges <span style={{ color: 'red' }}>*</span><span className="desc-precision">(Les liens URL vers des pièces graphiques ou des documents complémentaires peuvent être proposés par l’acheteur à titre d’information, mais seules les informations écrites ci-dessous sont contractuelles. Par ailleurs, toutes précisions administratives ou juridiques de nature à contrevenir à l’accord-cadre général sans dérogations prévues au contrat est a considérer comme inexistantes dans ce descriptif)</span></label>
                <textarea
                  value={commande?.formulaire?.besoin || ''}
                  onChange={(e) => handleBesoinChange(e.target.value)}
                  placeholder="Cahier des charges (obligatoire)"
                  rows={20}
                  className={errors.besoin ? 'error' : ''}
                />
                {errors.besoin && (
                  <span className="error-message">{errors.besoin}</span>
                )}
              </div>
            ) : (
              <div className="form-group">
                <label>Cahier des charges <span className="desc-precision">(Les liens URL vers des pièces graphiques ou des documents complémentaires peuvent être proposés par l’acheteur à titre d’information, mais seules les informations écrites ci-dessous sont contractuelles. Par ailleurs, toutes précisions administratives ou juridiques de nature à contrevenir à l’accord-cadre général sans dérogations prévues au contrat est a considérer comme inexistantes dans ce descriptif)</span></label>
                <div className="input-inactif definition-besoin">
                  {commande?.formulaire?.besoin || commande?.operation?.besoin || "Pas précisé"}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Descriptif Financier */}
        <section>
          <h3 className="section-header">Descriptif Financier</h3>
          <div className="section-content">
            {(() => {
              // CORRECTION: Vérifier le bon chemin pour transferred selon le rôle
              const isTransferred = isClientView ?
                commande?.transferred === true :
                commande?.operation?.transferred === true;

              // NOUVEAU: Vérifier si l'acheteur doit valider le transfert au rang suivant
              const statusNom = commande?.status?.nom || rangStatus;
              const isTransfertAcheteurValidation = isTransferred &&
                isClientView &&
                (rang === 2 || rang === 3) &&
                (statusNom === 'Brouillon' || statusNom === 'Brouillon acheteur' || statusNom === 'À analyser');

              console.log('=== DEBUG DESCRIPTIF FINANCIER ===');
              console.log('isTransferred:', isTransferred);
              console.log('isClientView:', isClientView);
              console.log('rang:', rang);
              console.log('statusNom:', statusNom);
              console.log('isTransfertAcheteurValidation:', isTransfertAcheteurValidation);

              // NOUVEAU: Si c'est une validation de transfert par l'acheteur, on saute le bloc isTransferred
              // et on utilise la logique normale comme si c'était rang 1
              if (isTransferred && !isTransfertAcheteurValidation) {
                // Opération transférée (SAUF validation acheteur rang 2/3)
                if (isClientView && canPrestataireAddArticles) {
                  // Pour l'acheteur : affichage statique
                  return (
                    <div className="prestataire-mode">
                      <div className="message-content">
                        <h4>Demande envoyée au prestataire de rang {rang}</h4>
                        <p>
                          Votre demande a été transmise au prestataire afin qu'il ajoute les articles nécessaires.
                          Vous serez informé quand vous pourrez valider sa sélection.
                        </p>
                        <div className="status-indicator">
                          <span className="status-dot"></span>
                          En attente de la réponse du prestataire
                        </div>
                      </div>
                    </div>
                  );
                } else if (isClientView && !canPrestataireAddArticles) {
                  return (
                    <div className="prestataire-mode">
                      <div className="message-content">
                        <h4>Consultation envoyée au prestataire de rang {rang}</h4>
                        <p>
                          Le prestataire a reçu votre consultation. Vous serez informé de sa réponse dans les 10 jours maximum.
                        </p>
                        <div className="status-indicator">
                          <span className="status-dot"></span>
                          Consultation reçue le {
                            (() => {
                              const reponseEvent = commande?.historique
                                ?.filter((h: any) => h.prefix === 'Consultation')
                                ?.sort((a: any, b: any) =>
                                  new Date(b.updated_at || b.created_at).getTime() -
                                  new Date(a.updated_at || a.created_at).getTime()
                                )?.[0];
                              return reponseEvent ? formatDate(reponseEvent.updated_at || reponseEvent.created_at) : 'N/A';
                            })()
                          }
                        </div>
                      </div>
                    </div>
                  );
                } else if (isPrestataireView && canPrestataireAddArticles) {
                  // CORRECTION: Pour les BC en mode "À chiffrer", ne rien afficher ici
                  // Le prestataire doit remplir les articles dans l'onglet OFFRE, pas Consultation
                  const isChiffrerMode = commande?.status?.nom === 'À chiffrer';

                  if (isChiffrerMode) {
                    // Pour les paniers inversés BC : pas de formulaire d'articles dans Consultation
                    return null;
                  }

                  // NOUVEAU: Pour les MS ou autres cas : utiliser exactement la même interface que les acheteurs
                  return (
                    <div className="prestataire-fill-articles">
                      {/* Instructions pour le prestataire */}
                      <div className="prestataire-instructions">
                        <h4>Remplir les articles pour cette opération</h4>
                        <p>L'acheteur vous a demandé de définir les articles nécessaires pour cette opération. Sélectionnez les articles appropriés dans le catalogue.</p>
                      </div>

                      {/* MODIFICATION: Utiliser exactement la même structure que les acheteurs */}
                      {hasExistingArticles ? (
                        <div className="articles-table-container">
                          <div className="articles-table-wrapper">
                            <table>
                              <thead>
                                <tr>
                                  <th>N° Réf</th>
                                  <th>Description de la prestation</th>
                                  <th>Unité</th>
                                  <th>Quantité</th>
                                  <th>Prix unitaire HT</th>
                                  <th>Total HT</th>
                                  {/* CORRECTION: Actions seulement si pas encore traité */}
                                  {(() => {
                                    const status = commande?.status?.nom;
                                    const statusId = commande?.status?.id;
                                    const prestataireHasActed =
                                      status === 'Accepté' || status === 'Valider' ||
                                      status === 'Refusé' || status === 'Refuser' ||
                                      status === 'Expiré' || status === 'Expirer' ||
                                      status === 'En attente de décision de l\'acheteur';
                                    return !prestataireHasActed ? <th>Actions</th> : null;
                                  })()}
                                </tr>
                              </thead>
                              <tbody>
                                {existingArticles.map((article, index) => (
                                  <tr key={article.id}>
                                    <td className="article-number">{article.code}</td>
                                    <td className="article-description">
                                      <div className="article-name">{article.nom}</div>
                                    </td>
                                    <td className="article-unit">{article.unite}</td>
                                    <td className="article-quantity">
                                      <div className="quantity-cell">{article.quantite}</div>
                                    </td>
                                    <td className="article-price price-cell">
                                      {formatPrice(ArticlesService.getPrixByQuantity(article, article.quantite), 4)}
                                    </td>
                                    <td className="article-total total-cell">
                                      {formatPrice(article.totalHT, 4)}
                                    </td>
                                    {/* CORRECTION: Actions seulement si pas encore traité */}
                                    {(() => {
                                      const status = commande?.status?.nom;
                                      const statusId = commande?.status?.id;
                                      const prestataireHasActed =
                                        status === 'Accepté' || status === 'Valider' ||
                                        status === 'Refusé' || status === 'Refuser' ||
                                        status === 'Expiré' || status === 'Expirer' ||
                                        status === 'En attente de décision de l\'acheteur';

                                      return !prestataireHasActed ? (
                                        <td className="article-actions">
                                          <button
                                            className="delete-button"
                                            onClick={() => handleRemoveExistingArticle(article.catalogueId || article.id)}
                                            type="button"
                                          >×</button>
                                        </td>
                                      ) : null;
                                    })()}
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {/* CORRECTION: Bouton ajouter seulement si pas encore traité */}
                            {(() => {
                              const status = commande?.status?.nom;
                              const statusId = commande?.status?.id;
                              const prestataireHasActed =
                                status === 'Accepté' || status === 'Valider' ||
                                status === 'Refusé' || status === 'Refuser' ||
                                status === 'Expiré' || status === 'Expirer' || status === 'En attente de décision de l\'acheteur';

                              return !prestataireHasActed ? (
                                <button
                                  className="add-article-button"
                                  type="button"
                                  onClick={() => setIsArticlesModalOpen(true)}
                                >Ajouter ou modifier un article</button>
                              ) : null;
                            })()}
                          </div>

                          {/* Section total - toujours visible */}
                          <div className="articles-actions">
                            <div className="total-section">
                              <p>Total HT de votre offre : {formatPrice(existingArticles.reduce((sum, article) => sum + article.totalHT, 0), 4)}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // MODIFICATION: Même choix initial que les acheteurs mais adapté au prestataire
                        <div className="financial-choice">
                          <h4>Définir les articles pour cette opération</h4>
                          <div className="choice-buttons">
                            <Button
                              value="Sélectionner des articles du catalogue"
                              variant="primary"
                              size="small"
                              onClick={() => {
                                console.log('Prestataire : ouverture du catalogue');
                                setIsArticlesModalOpen(true);
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // Prestataire sans droits ou statut différent
                  return (
                    <div className="prestataire-no-access">
                      <p>Vous n'avez pas accès à la modification des articles pour cette opération.</p>
                    </div>
                  );
                }
              } else {
                // GARDER: Logique existante pour les opérations non transférées
                if (isSectionReadOnly('financier')) {
                  // Mode lecture seule (consultation lancée normalement)
                  if (hasExistingArticles) {
                    // Afficher les articles existants
                    return (
                      <div className="articles-display">
                        <div className="articles-table-wrapper">
                          <table>
                            <thead>
                              <tr>
                                <th>N° Réf</th>
                                <th>Description de la prestation</th>
                                <th>Unité</th>
                                <th>Quantité</th>
                                <th>Prix unitaire HT</th>
                                <th>Total HT</th>
                              </tr>
                            </thead>
                            <tbody>
                              {existingArticles.map((article, index) => (
                                <tr key={article.id}>
                                  <td>{article.code}</td>
                                  <td>{article.nom}</td>
                                  <td>{article.unite}</td>
                                  <td>{article.quantite}</td>
                                  <td>{formatPrice(article.prix, 4)}</td>
                                  <td>{formatPrice(article.totalHT, 4)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="total-section">
                          <p>
                            Total HT (Rang {rang}) : {formatPrice(
                              existingArticles.reduce((sum, article) => sum + article.totalHT, 0), 4
                            )}
                          </p>

                        </div>
                      </div>
                    );
                  } else {
                    // Aucun article défini
                    return (
                      <div className="no-articles-message">
                        <p>Aucun article défini pour cette consultation.</p>
                      </div>
                    );
                  }
                } else {
                  // Mode édition (brouillon acheteur)
                  return (
                    <>
                      {/* Articles existants avec possibilité de modification */}
                      {hasExistingArticles && (
                        <div className="articles-table-container">
                          {/* NOUVEAU: Bouton Changer de mode en haut utilisant les classes existantes */}
                          {isClientView && (rangStatus === 'Brouillon' || commande?.status?.nom === 'Brouillon acheteur') && (
                            <div className="changer-de-mode">
                              <Button
                                value="Changer de mode"
                                variant="secondary"
                                size="small"
                                onClick={() => {
                                  if (existingArticles.length > 0) {
                                    setIsChangeModeModalOpen(true);
                                  } else {
                                    setArticlesMode('initial');
                                  }
                                }}
                              />
                            </div>
                          )}

                          <div className="articles-table-wrapper">
                            <table>
                              <thead>
                                <tr>
                                  <th>N° Réf</th>
                                  <th>Description de la prestation</th>
                                  <th>Unité</th>
                                  <th>Quantité</th>
                                  <th>Prix unitaire HT</th>
                                  <th>Total HT</th>
                                  {(isClientView && (rangStatus === 'Brouillon' || commande?.status?.nom === 'Brouillon acheteur')) && <th>Actions</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {existingArticles.map((article, index) => (
                                  <tr key={article.id}>
                                    <td className="article-number">{article.code}</td>
                                    <td className="article-description">
                                      <div className="article-name">{article.nom}</div>
                                    </td>
                                    <td className="article-unit">{article.unite}</td>
                                    <td className="article-quantity">
                                      <div className="quantity-cell">{article.quantite}</div>
                                    </td>
                                    <td className="article-price price-cell">
                                      {formatPrice(ArticlesService.getPrixByQuantity(article, article.quantite), 4)}
                                    </td>
                                    <td className="article-total total-cell">
                                      {formatPrice(article.totalHT, 4)}
                                    </td>
                                    {(isClientView && (rangStatus === 'Brouillon' || commande?.status?.nom === 'Brouillon acheteur')) && (
                                      <td className="article-actions">
                                        <button
                                          className="delete-button"
                                          onClick={() => handleRemoveExistingArticle(article.catalogueId || article.id)}
                                          type="button"
                                        >×</button>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {isClientView && (rangStatus === 'Brouillon' || commande?.status?.nom === 'Brouillon acheteur') && (
                              <button
                                className="add-article-button"
                                type="button"
                                onClick={() => {
                                  console.log('Clic sur ajouter un article (articles existants)');
                                  setIsArticlesModalOpen(true);
                                }}
                              >Ajouter ou modifier un article</button>
                            )}
                          </div>

                          <div className="articles-actions">
                            <div className="total-section">
                              <p>Total HT (Rang {rang}): {formatPrice(existingArticles.reduce((sum, article) => sum + article.totalHT, 0), 4)}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Choix initial si pas d'articles */}
                      {!hasExistingArticles && isClientView && (
                        <>
                          {articlesMode === 'initial' && (
                            <div className="financial-choice">
                              <h4>Comment souhaitez-vous définir les articles ?</h4>
                              <div className="choice-buttons">
                                <Button
                                  value="Choisir des articles du catalogue"
                                  variant="primary"
                                  size="small"
                                  onClick={() => {
                                    console.log('Clic sur choisir des articles du catalogue');
                                    setArticlesMode('catalogue');
                                    setIsArticlesModalOpen(true);
                                  }}
                                />
                                <Button
                                  value="Laisser le prestataire remplir les articles"
                                  variant="secondary"
                                  size="small"
                                  onClick={() => {
                                    setIsEnvoyerPrestataireModalOpen(true);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Affichage pour les autres cas */}
                      {/* {(!isClientView || rangStatus !== 'Brouillon') && !hasExistingArticles && (
                      <div className="info-message">
                        <p>Les articles seront définis selon les modalités de la consultation.</p>
                      </div>
                    )} */}
                    </>
                  );
                }
              }
            })()}
          </div>
        </section>

        <section>
          <h3 className='section-header'>Contact et documents</h3>
          <div className="section-content coordinates-section">
            <Coordonnees
              commande={isPrestataireView ? commande : undefined}
              operation={isPrestataireView ? undefined : commande}
              prestataire={isClientView ? commande.commandes[rang - 1] : undefined}
              isAcheteur={isClientView}
              isPresta={isPrestataireView}
              showSignataire={false}
            />
          </div>
        </section>

        {/* Section Validation - uniquement pour les acheteurs non-signataires */}
        {/* {isClientView && user?.role === 'Acheteur' && (
          <section>
            <h3 className="section-header">Validation</h3>
            <div className="section-content">
              <div className="form-group">
                <label>Sélectionner un validateur <span style={{ color: 'red' }}>*</span></label>
                {validateursLoading ? (
                  <p>Chargement des validateurs...</p>
                ) : (
                  <select
                    value={selectedValidateur || ''}
                    onChange={(e) => setSelectedValidateur(Number(e.target.value))}
                    required
                    disabled={isSectionReadOnly('admin')}
                  >
                    <option value="">Choisir un validateur</option>
                    {validateurs.map((validateur) => (
                      <option key={validateur.id} value={validateur.id}>
                        {validateur.prenom} {validateur.nom} ({validateur.role})
                      </option>
                    ))}
                  </select>
                )}
                {!selectedValidateur && (
                  <span className="error-message">Veuillez sélectionner un validateur</span>
                )}
              </div>
            </div>
          </section>
        )} */}

        {/* NOUVEAU: Section consultation-info - Affichage différent selon lancée/transférée */}
        {(() => {
          const isTransferred = commande?.transferred === true;
          const status = commande?.status?.nom;
          const statusId = commande?.id_status;
          const commandes = commande?.commandes || [];

          console.log('=== DEBUG AFFICHAGE CONSULTATION INFO ===');
          console.log('consultationStatus:', consultationStatus);
          console.log('commande.date_consultation:', commande?.date_consultation);
          console.log('commande.operateur:', commande?.operateur);
          console.log('status:', status);
          console.log('statusId:', statusId);

          // CORRECTION: Pour panier normal, vérifier si on est après un refus ou expiration de rang précédent
          const rangPrecedent = rang - 1;
          const cmdPrecedente = rangPrecedent > 0 ? commandes.find((cmd: any) => parseInt(cmd.rang) === rangPrecedent) : null;
          const rangPrecedentRefuseOuExpire = cmdPrecedente && (cmdPrecedente.id_status === 8 || cmdPrecedente.id_status === 9);
          const cmdCeRang = commandes.find((cmd: any) => parseInt(cmd.rang) === rang);
          const cmdCeRangEnBrouillon = cmdCeRang && cmdCeRang.id_status === 1;

          // CORRECTION: Ne pas afficher la notification de consultation si :
          // - Panier normal ET rang > 1 ET rang précédent refusé/expiré ET commande actuelle en brouillon
          const shouldHideConsultationNotif = !isTransferred && rang > 1 && rangPrecedentRefuseOuExpire && cmdCeRangEnBrouillon && (status === 'À analyser' || statusId === 12);

          console.log('shouldHideConsultationNotif:', shouldHideConsultationNotif);

          if (shouldHideConsultationNotif) {
            console.log('⚠️ Masquage de la notification de consultation - Rang précédent refusé/expiré, consultation non lancée');
            return null;
          }

          // CORRECTION: Afficher même si date_consultation ou operateur ne sont pas encore définis
          // Utiliser des valeurs par défaut pour éviter l'affichage vide
          if (consultationStatus.isVisible && commande) {
            const dateConsultation = commande.date_consultation || formatDate(commande.updated_at || commande.created_at);
            const operateur = commande?.cree_par || commande?.operation?.cree_par || commande?.organisme?.nom || 'Organisme';

            console.log('✅ Affichage de la consultation info');
            console.log('dateConsultation:', dateConsultation);
            console.log('operateur:', operateur);

            return (
              <>
                <section>
                  {consultationStatus.type === 'transferee' ? (
                    <div className="section-content consultation-info">
                      <p>
                        Consultation lancée au rang {rang} le {dateConsultation} par{' '}
                        {operateur}
                      </p>
                    </div>
                  ) : (
                    <div className="section-content consultation-info">
                      <p>
                        {`Consultation lancée au rang ${rang} le ${dateConsultation} par ${operateur}`}
                      </p>
                    </div>
                  )}


                  {/* NOUVEAU: Message d'offre envoyée si applicable */}
                  {consultationStatus.showOffreEnvoyee && (
                    <div className="section-content consultation-info">
                      <p className="offre-envoyee-message">
                        {`Offre envoyée le ${commande?.updated_at ? formatDate(commande.updated_at) : 'date non disponible'} par le prestataire ${commande?.contrat?.entite?.nom || commande?.operation?.organisme?.nom || commande?.prestataire?.nom || 'Prestataire'} de rang ${rang}`}
                      </p>
                    </div>
                  )}
                </section>
              </>
            );
          }

          console.log('❌ Consultation info non affichée - consultationStatus.isVisible:', consultationStatus.isVisible);
          return null;
        })()}

        {/* Boutons d'actions */}
        <div className="action-buttons">
          {/* CORRECTION: Actions pour acheteur - panier inversé ET panier normal */}
          {!isOperationExpired && (() => {
            const isTransferred = commande?.transferred === true;
            const status = commande?.status?.nom;
            const statusId = commande?.status?.id;
            const commandes = commande?.commandes || [];

            console.log('=== VÉRIFICATION AFFICHAGE BOUTONS ===');
            console.log('isClientView:', isClientView);
            console.log('isTransferred:', isTransferred);
            console.log('rang (prop):', rang);
            console.log('Status:', status);
            console.log('Commandes array:', commandes);

            if (!isClientView) {
              console.log('❌ Boutons cachés - Pas un client');
              return null;
            }

            // Chercher la commande pour ce rang
            const cmdCeRang = commandes.find((cmd: any) => parseInt(cmd.rang) === rang);

            // Vérifier si le prestataire a envoyé son offre (pour panier inversé)
            const prestataireHasSentOffre = cmdCeRang && (cmdCeRang.id_status === 13 || cmdCeRang.id_status === 4);

            // CORRECTION: Vérifier si on peut passer au rang suivant
            const rangPrecedent = rang - 1;
            const cmdPrecedente = rangPrecedent > 0 ? commandes.find((cmd: any) => parseInt(cmd.rang) === rangPrecedent) : null;
            // CORRECTION: Inclure les commandes expirées (id_status === 9) en plus des refusées (id_status === 8)
            const rangPrecedentRefuse = cmdPrecedente && (cmdPrecedente.id_status === 8 || cmdPrecedente.id_status === 9);

            // CORRECTION: Le rang suivant doit être en brouillon (id_status === 1) pour pouvoir être consulté
            const cmdCeRangEnBrouillon = cmdCeRang && cmdCeRang.id_status === 1;
            const peutPasserAuRangSuivant = rangPrecedentRefuse &&
              rang < 4 && // Rang 2 ou 3
              (status === 'À analyser' || statusId === 12) && // Statut "À analyser"
              cmdCeRangEnBrouillon; // Le rang actuel est en brouillon

            console.log('=== BOUTONS ACHETEUR ===');
            console.log('Type:', isTransferred ? 'PANIER INVERSÉ' : 'PANIER NORMAL');
            console.log('Rang:', rang);
            console.log('Commande ce rang:', cmdCeRang);
            console.log('Rang précédent refusé:', rangPrecedentRefuse);
            console.log('Commande ce rang en brouillon:', cmdCeRangEnBrouillon);
            console.log('Status:', status, '(ID:', statusId, ')');
            console.log('Peut passer au rang suivant:', peutPasserAuRangSuivant);
            console.log('Prestataire a envoyé offre:', prestataireHasSentOffre);

            // CAS 1: Le prestataire a envoyé son offre (rang actuel) → Accepter/Refuser (PANIER INVERSÉ uniquement)
            if (isTransferred && prestataireHasSentOffre) {
              return (
                <div className="action-buttons-group">
                  <Button
                    value={`Refuser l'offre (Rang ${rang})`}
                    variant="secondary"
                    size="small"
                    onClick={() => handleRefuserCommande(rang)}
                  />
                  <Button
                    value={`Accepter l'offre (Rang ${rang})`}
                    variant="primary"
                    size="small"
                    onClick={() => handleAccepterCommande(rang)}
                  />
                  <PrintButton
                    targetRef={printRef}
                    fileName={`${commande?.numero || commande?.operation?.numero} - Consultation Rang ${rang}`}
                    value="PDF"
                    suiviHeaderProps={{
                      titre: commande.operation?.nom || commande?.nom,
                      sousTitre: getSousTitre(), // fonction disponible dans le parent
                      subtitle: getSubtitle(),   // fonction disponible dans le parent
                      status: getDisplayStatus(),
                      statusColor: getStatusColor(getDisplayStatus()),
                      delais: commande.expire_at,
                      backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
                    }}
                  />
                </div>
              );
            }

            // CAS 2: Rang précédent refusé, on peut passer au rang suivant → Consulter le rang X / Annuler l'opération
            // CORRECTION: Cette logique s'applique aussi pour panier NORMAL après refus de rang précédent
            const isNormalBasket = !isTransferred;
            const shouldShowConsultationButtons = isNormalBasket && rang > 1 && rangPrecedentRefuse && cmdCeRangEnBrouillon && (status === 'À analyser' || statusId === 12);

            if (peutPasserAuRangSuivant || shouldShowConsultationButtons) {
              console.log('✅ Affichage des boutons: Annuler / Consulter le rang', rang);
              return (
                <div className="action-buttons-group">
                  <Button
                    value="Annuler l'opération"
                    variant="secondary"
                    size="small"
                    onClick={async () => {
                      if (window.confirm(`Êtes-vous sûr de vouloir annuler cette opération ? Cette action est irréversible.`)) {
                        try {
                          const token = localStorage.getItem('token');
                          if (!token) {
                            throw new Error('Token d\'authentification manquant');
                          }

                          // CORRECTION: Utiliser l'ID de l'opération et l'ID de la commande du rang actuel
                          const operationId = commande?.id;
                          const commandeId = cmdCeRang?.id;

                          if (!operationId || !commandeId) {
                            throw new Error('ID de l\'opération ou de la commande non trouvé');
                          }

                          console.log('=== ANNULATION OPÉRATION ===');
                          console.log('Operation ID:', operationId);
                          console.log('Commande ID (rang', rang, '):', commandeId);

                          // CORRECTION: Utiliser la route existante pour refuser l'opération
                          const response = await fetch(
                            `${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/refuse/${commandeId}`,
                            {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                                'Accept': 'application/json'
                              }
                            }
                          );

                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({}));
                            throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
                          }

                          const result = await response.json();
                          console.log('Opération annulée avec succès:', result);

                          alert('Opération annulée avec succès.');

                          // Rediriger vers la liste des opérations
                          window.location.href = '/operations';

                        } catch (error) {
                          console.error('Erreur lors de l\'annulation de l\'opération:', error);
                          const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'annulation';
                          alert(`Erreur: ${errorMessage}. Veuillez réessayer.`);
                        }
                      }
                    }}
                  />
                  <Button
                    value={`Consulter le prestataire de rang ${rang}`}
                    variant="primary"
                    size="small"
                    onClick={() => {
                      // Ouvrir le modal de confirmation avant de lancer la consultation
                      setPendingRangToConsult(rang);
                      setIsConfirmConsultationModalOpen(true);
                    }}
                  />
                  <PrintButton
                    targetRef={printRef}
                    fileName={`${commande?.numero || commande?.operation?.numero} - Consultation Rang ${rang}`}
                    value="PDF"
                    suiviHeaderProps={{
                      titre: commande.operation?.nom || commande?.nom,
                      sousTitre: getSousTitre(), // fonction disponible dans le parent
                      subtitle: getSubtitle(),   // fonction disponible dans le parent
                      status: getDisplayStatus(),
                      statusColor: getStatusColor(getDisplayStatus()),
                      delais: commande.expire_at,
                      backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
                    }}
                  />
                </div>
              );
            }

            console.log('⚠️ Aucun bouton affiché - Aucune condition remplie');
            return null;
          })
            ()}


          {/* Modal de confirmation pour consulter un prestataire de rang (ex: rang 2) */}
          <ValidationPopUp
            isOpen={isConfirmConsultationModalOpen}
            title={`Consulter le prestataire de rang ${pendingRangToConsult ?? ''}`}
            text={`Êtes-vous sûr de vouloir lancer la consultation pour le prestataire de rang ${pendingRangToConsult ?? ''} ?`}
            onClose={() => {
              setIsConfirmConsultationModalOpen(false);
              setPendingRangToConsult(null);
            }}
            onConfirm={async () => {
              // Exécuter l'action de lancement pour le rang sélectionné
              setIsConfirmConsultationModalOpen(false);
              const rangToRun = pendingRangToConsult;
              setPendingRangToConsult(null);

              if (!rangToRun) return;

              try {
                const token = localStorage.getItem('token');
                if (!token) throw new Error('Token d\'authentification manquant');

                const operationId = commande?.id;
                if (!operationId) throw new Error('ID de l\'opération non trouvé');

                const isPanierInverse = commande.transferred === true;

                let apiUrl: string;
                let method: string;

                if (isPanierInverse) {
                  apiUrl = `${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/transfertPrestataire`;
                  method = 'POST';
                } else {
                  apiUrl = `${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/consultation`;
                  method = 'PUT';
                }

                const response = await fetch(apiUrl, {
                  method,
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                  }
                });

                if (!response.ok) {
                  const errorData = await response.json().catch(() => ({}));
                  throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
                }

                const result = await response.json();
                console.log('Consultation lancée avec succès:', result);

                // Rafraîchir la page pour refléter l'état
                window.location.reload();
              } catch (error) {
                console.error('Erreur lors du lancement de la consultation:', error);
                const errorMessage = error instanceof Error ? error.message : 'Erreur lors du lancement';
                alert(`Erreur: ${errorMessage}. Veuillez réessayer.`);
              }
            }}
            cancelLabel="Non"
            confirmLabel="Oui"
          />

          {/* CORRECTION: Boutons d'action seulement si pas en lecture seule ET pas en attente de validation d'offre */}
          {(() => {
            const isTransferred = commande?.transferred === true;
            const commandes = commande?.commandes || [];
            const cmdCeRang = commandes.find((cmd: any) => parseInt(cmd.rang) === rang);
            const prestataireHasSentOffre = cmdCeRang && cmdCeRang.id_status === 13;

            // Ne pas afficher les boutons de lancement si on attend une décision sur une offre
            if (isTransferred && prestataireHasSentOffre) {
              return null;
            }

            return !isSectionReadOnly('admin') && isClientView ? (
              <div className="action-buttons-group">
                <div className="action-buttons-row">
                  <Button
                    value="Sauvegarder et quitter"
                    variant="secondary"
                    size="small"
                    onClick={handleSaveAndQuit}
                    disabled={isSaving}
                  />
                  {isSignataireRole(user?.role?.toString() ?? "") ? (
                    <Button
                      value={`Lancer la consultation (Rang ${rang})`}
                      variant="primary"
                      size="small"
                      onClick={handleLancer}
                      disabled={articlesMode === 'prestataire' || isSaving} // NOUVEAU: Désactiver pendant la sauvegarde
                      title={
                        !acceptCGV
                          ? "Vous devez accepter les CG"
                          : !hasArticles()
                            ? "Vous devez sélectionner au moins un article"
                            : articlesMode === 'prestataire'
                              ? 'Les articles doivent être définis par le prestataire avant de lancer la consultation'
                              : ""
                      }
                    />
                  ) : (
                    <Button
                      value={`Obtenir la validation de l'acheteur signataire`}
                      variant="primary"
                      size="small"
                      onClick={handleNotifyResponsable}
                      disabled={articlesMode === 'prestataire' || isSaving}
                      title={
                        !acceptCGV
                          ? "Vous devez accepter les CG"
                          : !hasArticles()
                            ? "Vous devez sélectionner au moins un article"
                            : articlesMode === 'prestataire'
                              ? 'Les articles doivent être définis par le prestataire avant de lancer la consultation'
                              : ""
                      }
                    />
                  )}
                  <PrintButton
                    targetRef={printRef}
                    fileName={`${commande?.numero || commande?.operation?.numero} - Consultation Rang ${rang}`}
                    value="PDF"
                    suiviHeaderProps={{
                      titre: commande.operation?.nom || commande?.nom,
                      sousTitre: getSousTitre(), // fonction disponible dans le parent
                      subtitle: getSubtitle(),   // fonction disponible dans le parent
                      status: getDisplayStatus(),
                      statusColor: getStatusColor(getDisplayStatus()),
                      delais: commande.expire_at,
                      backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
                    }}
                  />
                </div>
                {!isSignataireRole(user?.role?.toString() ?? "") && notificationInfo.sent && notificationInfo.labels.length > 0 && (
                  <div className="notifications-sent-list">
                    {notificationInfo.labels.map((label: string, index: number) => (
                      <div key={index} className="notification-sent-info">
                        <span className="notification-sent-icon">✓</span>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null;
          })()}

          {/* NOUVEAU: Actions pour prestataires transférés - seulement si pas encore traité */}
          {/* {(() => {
            const isTransferred = isClientView ?
              commande?.transferred === true :
              commande?.operation?.transferred === true;

            const status = commande?.status?.nom;
            const statusId = commande?.status?.id;

            // NOUVEAU: Vérifier si le prestataire a déjà traité l'opération
            const prestataireHasActed =
              status === 'Accepté' || status === 'Valider' ||
              status === 'Refusé' || status === 'Refuser' ||
              status === 'Expiré' || status === 'Expirer' || status === 'En attente de décision de l\'acheteur';

            return isPrestataireView && isTransferred && canPrestataireAddArticles && !prestataireHasActed ? (
              <div className="action-buttons-group">
                <Button
                  value="Refuser l'opération"
                  variant="secondary"
                  size="small"
                  onClick={() => setIsRefuserTransfertModalOpen(true)}
                />
                <Button
                  value="Envoyer l'offre"
                  variant="primary"
                  size="small"
                  onClick={handleEnvoyerOffreAvecCGV}
                  disabled={!hasArticles() || existingArticles.length === 0}
                  title={!hasArticles() || existingArticles.length === 0 ? "Ajoutez au moins un article pour envoyer votre offre" : ""}
                />
              </div>
            ) : null;
          })()} */}

          {/* CORRECTION: Actions existantes pour les prestataires normaux - seulement si pas encore traité */}
          {!isOperationExpired && (() => {
            const isTransferred = isClientView ?
              commande?.transferred === true :
              commande?.operation?.transferred === true;

            const status = commande?.status?.nom;
            const statusId = commande?.status?.id;

            // NOUVEAU: Vérifier si le prestataire a déjà traité l'opération
            const prestataireHasActed =
              status === 'Accepté' || status === 'Valider' ||
              status === 'Refusé' || status === 'Refuser' ||
              status === 'Expiré' || status === 'Expirer' || status === 'En attente de decision';

            return isPrestataireView && !isTransferred && !prestataireHasActed ? (
              <div className="prestataire-actions">
                {(rangStatus === 'En consultation' ||
                  status === 'En consultation' ||
                  status === `En attente de l'acceptation du rang ${rang}`) ? (
                  <>
                    <Button
                      value="Refuser l'Opération"
                      variant="secondary"
                      size="small"
                      onClick={() => setIsRefuseModalOpen(true)}
                    />
                    <Button
                      value="Accepter l'Opération"
                      variant="primary"
                      size="small"
                      onClick={handleAccepterAvecCGV}
                      title={!acceptCGV ? "Vous devez accepter les CG" : ""}
                    />
                    <PrintButton
                      targetRef={printRef}
                      fileName={`${commande?.numero || commande?.operation?.numero} - Consultation Rang ${rang}`}
                      value="PDF"
                      suiviHeaderProps={{
                        titre: commande.operation?.nom || commande?.nom,
                        sousTitre: getSousTitre(), // fonction disponible dans le parent
                        subtitle: getSubtitle(),   // fonction disponible dans le parent
                        status: getDisplayStatus(),
                        statusColor: getStatusColor(getDisplayStatus()),
                        delais: commande.expire_at,
                        backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
                      }}
                    />
                  </>
                ) : null}
              </div>
            ) : null;
          })()}

          {/* NOUVEAU: Messages de statut pour opérations déjà traitées */}
          {(() => {
            const status = commande?.status?.nom;
            const statusId = commande?.status?.id;

            const prestataireHasActed =
              status === 'Accepté' || status === 'Valider' ||
              status === 'Refusé' || status === 'Refuser' ||
              status === 'Expiré' || status === 'Expirer' || status === 'En attente de décision de l\'acheteur';

            return isPrestataireView && prestataireHasActed ? (
              <div className="prestataire-actions-readonly">
                {(status === 'Accepté' || status === 'Valider' || statusId === 11) && (
                  <div className="status-message accepted">
                    ✓ Offre envoyée et acceptée
                  </div>
                )}
                {(status === 'Refusé' || status === 'Refuser' || statusId === 6) && (
                  <div className="status-message refused">
                    ✗ Opération refusée
                  </div>
                )}
                {(status === 'Expiré' || status === 'Expirer' || statusId === 12) && (
                  <div className="status-message expired">
                    ⏰ Opération expirée
                  </div>
                )}
              </div>
            ) : null;
          })()}
        </div>

        {/* Modal Articles - CORRECTION: Même logique pour acheteurs ET prestataires */}
        {isArticlesModalOpen && (
          <ModalArticles
            isOpen={isArticlesModalOpen}
            onClose={() => {
              console.log('Fermeture du modal articles');
              setIsArticlesModalOpen(false);

              // NOUVEAU: Si aucun article existant et qu'on ferme le modal, revenir au mode initial
              if (!hasExistingArticles && existingArticles.length === 0) {
                console.log('Aucun article sélectionné - retour au mode initial');
                setArticlesMode('initial');
              }
            }}
            // CORRECTION: Pour les prestataires transférés, utiliser la même structure que les acheteurs
            commandes={(() => {
              // NOUVEAU: Adapter les données selon le rôle pour utiliser la même API
              if (isPrestataireView && commande?.operation?.transferred === true) {
                // Pour les prestataires dans une opération transférée, construire la structure attendue
                console.log('=== PRESTATAIRE TRANSFERT - Construction commandes pour ModalArticles ===');
                console.log('Commande complète:', commande);
                console.log('Operation dans commande:', commande.operation);

                // CORRECTION: Pour les prestataires transférés, utiliser les commandes de l'opération
                // mais faire en sorte que ModalArticles utilise l'API /bdc/commande/{id}/catalogue
                const commandesForModal = [{
                  id: commande.id, // ID de la commande prestataire (pour l'API)
                  rang: commande.rang || rang,
                  id_contrat: commande.id_contrat || commande.contrat?.id,
                  operation: commande.operation, // Contient lot_technique, lot_departement, etc.
                  // NOUVEAU: Copier les informations importantes depuis l'opération
                  lot_technique: commande.operation?.lot_technique,
                  lot_departement: commande.operation?.lot_departement,
                  // MARKER: Indiquer que c'est un prestataire pour l'API
                  _isPrestataireContext: true
                }];

                console.log('Commandes construites pour ModalArticles (prestataire):', commandesForModal);
                return commandesForModal;
              } else {
                // Pour les acheteurs ou autres cas, utiliser la structure normale
                return commande?.commandes || [];
              }
            })()}
            // CORRECTION: Utiliser le nom du lot depuis l'opération pour les prestataires transférés
            lotNom={(() => {
              if (isPrestataireView && commande?.operation?.transferred === true) {
                return commande.operation?.lot_technique?.nom || 'Catalogue';
              } else {
                return commande?.lot_technique?.nom || 'Catalogue';
              }
            })()}
            onAddToCommand={handleAddToCommand}
            existingSelection={existingArticles}
            // CORRECTION: Adapter prestataireRang pour les prestataires transférés
            prestataireRang={(() => {
              if (isPrestataireView && commande?.operation?.transferred === true) {
                return commande.rang || rang;
              } else {
                return getRangFromCommandes;
              }
            })()}
          />
        )}

        {/* NOUVEAU: Modal CGV (reprise exacte de Consultation.tsx) */}
        <CGVAccept
          acceptCGV={acceptCGV}
          setAcceptCGV={setAcceptCGV}
          isOpen={isCGVModalOpen}
          onClose={() => {
            setIsCGVModalOpen(false);
            // Réinitialiser les états si l'utilisateur ferme sans accepter
            if (readyToLaunchAfterCGV) {
              setReadyToLaunchAfterCGV(false);
            }
            if (readyToAcceptAfterCGV) {
              setReadyToAcceptAfterCGV(false);
            }
            if (readyToTransfertAfterCGV) {
              setReadyToTransfertAfterCGV(false);
            }
          }}
          onConfirm={async () => {
            // Si l'utilisateur a accepté les CGV et était prêt à lancer la consultation
            if (acceptCGV && readyToLaunchAfterCGV) {
              setReadyToLaunchAfterCGV(false);
              // Afficher la pop-up de confirmation après un court délai
              setTimeout(() => handleConfirmLaunch(), 100);
            }
            // NOUVEAU: Si l'utilisateur a accepté les CGV et était prêt à accepter l'opération
            else if (acceptCGV && readyToAcceptAfterCGV) {
              setReadyToAcceptAfterCGV(false);
              // Afficher la pop-up de confirmation après un court délai
              setTimeout(() => handleConfirmAccept(), 100);
            }
            // NOUVEAU: Si l'utilisateur a accepté les CGV et était prêt à envoyer l'offre
            else if (acceptCGV && readyToSendAfterCGV) {
              setReadyToSendAfterCGV(false);
              setTimeout(() => setIsEnvoyerOffreModalOpen(true), 100);
            }
            // NOUVEAU: Si l'utilisateur a accepté les CGV et était prêt à transférer au prestataire
            else if (acceptCGV && readyToTransfertAfterCGV) {
              setReadyToTransfertAfterCGV(false);
              // Fermer le modal CGV d'abord
              setIsCGVModalOpen(false);
              // Réouvrir le modal de confirmation du transfert
              setTimeout(() => setIsEnvoyerPrestataireModalOpen(true), 100);
            }
          }}
        />

        {/* Tous les autres modals restent identiques */}
        <ValidationPopUp
          isOpen={isConfirmModalOpen}
          title="Lancement de la consultation"
          text={`Vous vous apprêtez à lancer la consultation pour le prestataire de rang ${rang}. Cette action est irréversible.`}
          onClose={() => setIsConfirmModalOpen(false)}

          onConfirm={async () => {

            setIsConfirmModalOpen(false);
            // CORRECTION: Utiliser la nouvelle fonction au lieu de handleLancerConsultation
            await handleConfirmLancerConsultation();
          }}
          cancelLabel="Annuler"
          confirmLabel="Confirmer"
        />

        {/* Modal envoyer au prestataire */}
        <ModalEnvoyerPrestataire />

        {/* Modal changer de mode */}
        <ModalChangerMode />

        {/* Modal envoyer offre */}
        <ModalEnvoyerOffre />

        {/* Modal refuser transfert */}
        <ModalRefuserTransfert />

        {/* Modal accepter commande (prestataire) - garder existant */}
        <ModalAccepterCommande />

        {/* Modal refuser commande (prestataire) - garder existant */}
        <ModalRefuserCommande />

        {/* Modal notification responsable */}
        <ModalNotifyResponsable />
      </div>

      {/* <div className='print-button-container'>
        <PrintButton
          targetRef={printRef}
          fileName={`${commande?.numero || commande?.operation?.numero} - Offre`}
          value="PDF"
          suiviHeaderProps={{
            titre: commande.operation?.nom || commande?.nom,
            sousTitre: getSousTitre(), // fonction disponible dans le parent
            subtitle: getSubtitle(),   // fonction disponible dans le parent
            status: getDisplayStatus(),
            statusColor: getStatusColor(getDisplayStatus()),
            delais: commande.expire_at,
            backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
          }}
        />
      </div> */}
    </>
  );
});

// Ajouter un displayName pour le debugging
ConsultationBC.displayName = 'ConsultationBC';

export default ConsultationBC;